import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ApiClient,
  ProblemDetailError,
  type AIProviderConfigWire,
  type AIProviderPreflightResponseWire,
  type CryptoExchangeCapabilityListResponseWire,
  type ForexAccountWire,
  type ForexConfigWire,
  type CryptoPaperTradingConfigWire,
  type CryptoPaperValidationDayRequest,
  type CryptoPaperValidationGateResponse,
  type PendingLiveAccount,
  type PositionReassessmentConfigWire,
  type TelegramAgentConfigWire,
  type TelegramAgentStatusWire,
  type TelegramAgentTestConnectionResponseWire,
  type WizardCredentialsRequest,
} from "../api/client";
import { useFixtures } from "../api/dataSource";
import type {
  Account,
  EventRow,
  Pending,
  PendingConfigChange,
  Position,
  RiskLimit,
  RiskLimitKey,
  SectorLimits,
  SectorLimitsUpdate,
  StrategyRoute,
  SystemHealthRow,
} from "../types";
import { relativeUntil } from "../lib/coolingOff";

// Configuration screen — UX §13.
// The tab bodies below use only the DataSource-backed state that is
// currently wired in production: accounts, positions, pending approvals,
// risk limits, system health, and recent audit events. Domains without
// a backend contract yet render an explicit unavailable state instead of
// a Phase-4-era placeholder.

const TABS: { key: ConfigTab; label: string }[] = [
  { key: "risk", label: "Risk" },
  { key: "accounts", label: "Accounts" },
  { key: "data", label: "Data" },
  { key: "channels", label: "Channels" },
  { key: "ai", label: "AI" },
  { key: "strategies", label: "Strategies" },
  { key: "universe", label: "Universe" },
  { key: "lenses", label: "Lenses" },
  { key: "baskets", label: "Baskets" },
  { key: "system", label: "System" },
];

export type ConfigTab =
  | "risk"
  | "accounts"
  | "data"
  | "channels"
  | "ai"
  | "strategies"
  | "universe"
  | "lenses"
  | "baskets"
  | "system";

type Props = {
  tab: ConfigTab;
  onTab: (tab: ConfigTab) => void;
  riskLimits: RiskLimit[];
  pendingChanges: PendingConfigChange[];
  coolingOffDays: number;
  onCoolingOffDaysChange: (days: number) => void;
  accounts: Account[];
  positions: Position[];
  pending: Pending[];
  systemHealth: SystemHealthRow[];
  recentEvents: EventRow[];
  listRiskLimits: (brokerAccountId?: string) => Promise<RiskLimit[]>;
  getSectorLimits: (brokerAccountId: string) => Promise<SectorLimits>;
  setSectorLimits: (args: SectorLimitsUpdate) => Promise<SectorLimits>;
  listStrategyRoutes: () => Promise<StrategyRoute[]>;
  updateStrategyRoute: (args: {
    strategyId: string;
    brokerAccountId: string;
    operatorNote?: string | null;
  }) => Promise<StrategyRoute>;
  onTighten: (key: RiskLimitKey, newValue: number) => void;
  onOpenLoosen: (key: RiskLimitKey) => void;
  onCancelPending: (id: string) => void;
  /** Phase 7a D8: quality-tier warning for the AI tab. */
  newsProviderName?: string;
  qualityTierWarning?: boolean;
  apiClient: ApiClient;
};

export const ConfigScreen = ({
  tab,
  onTab,
  riskLimits,
  pendingChanges,
  coolingOffDays,
  onCoolingOffDaysChange,
  accounts,
  positions,
  pending,
  systemHealth,
  recentEvents,
  listRiskLimits,
  getSectorLimits,
  setSectorLimits,
  listStrategyRoutes,
  updateStrategyRoute,
  onTighten,
  onOpenLoosen,
  onCancelPending,
  newsProviderName = "none",
  qualityTierWarning = false,
  apiClient,
}: Props) => {
  return (
    <div data-screen-label="Config">
      <div className="tp-page-head">
        <h1>Configuration</h1>
        <div className="meta">
          {pendingChanges.length > 0
            ? `${pendingChanges.length} pending change${pendingChanges.length === 1 ? "" : "s"}`
            : "No pending changes"}
        </div>
      </div>

      <div className="tp-config-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={"tp-config-tab" + (tab === t.key ? " active" : "")}
            onClick={() => onTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "risk" && (
        <RiskTab
          riskLimits={riskLimits}
          pendingChanges={pendingChanges}
          coolingOffDays={coolingOffDays}
          onCoolingOffDaysChange={onCoolingOffDaysChange}
          accounts={accounts}
          listRiskLimits={listRiskLimits}
          getSectorLimits={getSectorLimits}
          setSectorLimits={setSectorLimits}
          onTighten={onTighten}
          onOpenLoosen={onOpenLoosen}
          onCancelPending={onCancelPending}
          apiClient={apiClient}
        />
      )}
      {tab === "accounts" && (
        <AccountsTab
          accounts={accounts}
          positions={positions}
          apiClient={apiClient}
        />
      )}
      {tab === "data" && (
        <DataTab
          apiClient={apiClient}
          systemHealth={systemHealth}
          recentEvents={recentEvents}
        />
      )}
      {tab === "channels" && (
        <ChannelsTab apiClient={apiClient} accounts={accounts} />
      )}
      {tab === "ai" && (
        <AiTab
          apiClient={apiClient}
          systemHealth={systemHealth}
          pending={pending}
          recentEvents={recentEvents}
          newsProviderName={newsProviderName}
          qualityTierWarning={qualityTierWarning}
        />
      )}
      {tab === "strategies" && (
        <StrategiesTab
          pending={pending}
          positions={positions}
          recentEvents={recentEvents}
          listStrategyRoutes={listStrategyRoutes}
          updateStrategyRoute={updateStrategyRoute}
        />
      )}
      {tab === "universe" && (
        <UniverseTab
          pending={pending}
          positions={positions}
          recentEvents={recentEvents}
        />
      )}
      {tab === "lenses" && (
        <LensesTab pending={pending} recentEvents={recentEvents} />
      )}
      {tab === "baskets" && (
        <BasketsTab pending={pending} recentEvents={recentEvents} />
      )}
      {tab === "system" && (
        <SystemTab systemHealth={systemHealth} recentEvents={recentEvents} />
      )}
    </div>
  );
};

// --- Risk tab body ----------------------------------------------------

type RiskTabProps = {
  riskLimits: RiskLimit[];
  pendingChanges: PendingConfigChange[];
  coolingOffDays: number;
  onCoolingOffDaysChange: (days: number) => void;
  accounts: Account[];
  listRiskLimits: (brokerAccountId?: string) => Promise<RiskLimit[]>;
  getSectorLimits: (brokerAccountId: string) => Promise<SectorLimits>;
  setSectorLimits: (args: SectorLimitsUpdate) => Promise<SectorLimits>;
  onTighten: (key: RiskLimitKey, newValue: number) => void;
  onOpenLoosen: (key: RiskLimitKey) => void;
  onCancelPending: (id: string) => void;
  apiClient: ApiClient;
};

const STANDARD_GICS_SECTORS = [
  "Communication Services",
  "Consumer Discretionary",
  "Consumer Staples",
  "Energy",
  "Financials",
  "Health Care",
  "Industrials",
  "Information Technology",
  "Materials",
  "Real Estate",
  "Utilities",
] as const;

const pctDraftIsValid = (raw: string) => {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 && n <= 100;
};

const RiskTab = ({
  riskLimits,
  pendingChanges,
  coolingOffDays,
  onCoolingOffDaysChange,
  accounts,
  listRiskLimits,
  getSectorLimits,
  setSectorLimits,
  onTighten,
  onOpenLoosen,
  onCancelPending,
  apiClient,
}: RiskTabProps) => {
  const [editingKey, setEditingKey] = useState<RiskLimitKey | null>(null);
  const [tightenValue, setTightenValue] = useState<string>("");
  const [tightenError, setTightenError] = useState<string | null>(null);
  const [accountRiskLimits, setAccountRiskLimits] =
    useState<RiskLimit[]>(riskLimits);
  const [selectedBrokerAccountId, setSelectedBrokerAccountId] =
    useState<string>("");
  const [sectorExpanded, setSectorExpanded] = useState(false);
  const [sectorLimits, setSectorLimitsState] = useState<SectorLimits | null>(
    null,
  );
  const [sectorDefaultDraft, setSectorDefaultDraft] = useState("30");
  const [sectorDrafts, setSectorDrafts] = useState<Record<string, string>>({});
  const [sectorOverrideDrafts, setSectorOverrideDrafts] =
    useState<Record<string, boolean>>({});
  const [sectorLoading, setSectorLoading] = useState(false);
  const [sectorSaving, setSectorSaving] = useState(false);
  const [sectorError, setSectorError] = useState<string | null>(null);
  const [coolingOffEditing, setCoolingOffEditing] = useState(false);
  const [coolingOffDraft, setCoolingOffDraft] = useState(String(coolingOffDays));
  const [coolingOffSaving, setCoolingOffSaving] = useState(false);
  const [coolingOffError, setCoolingOffError] = useState<string | null>(null);

  const accountOptions = accounts.filter((account) => (
    account.assetClass ?? "equity"
  ) !== "crypto");
  const selectableAccounts =
    accountOptions.length > 0 ? accountOptions : accounts;

  useEffect(() => {
    setAccountRiskLimits(riskLimits);
  }, [riskLimits]);

  useEffect(() => {
    if (!coolingOffEditing) {
      setCoolingOffDraft(String(coolingOffDays));
    }
  }, [coolingOffDays, coolingOffEditing]);

  useEffect(() => {
    if (selectableAccounts.length === 0) {
      setSelectedBrokerAccountId("");
      return;
    }
    const stillPresent = selectableAccounts.some(
      (account) => account.brokerAccountId === selectedBrokerAccountId,
    );
    if (!selectedBrokerAccountId || !stillPresent) {
      setSelectedBrokerAccountId(selectableAccounts[0].brokerAccountId);
    }
  }, [selectableAccounts, selectedBrokerAccountId]);

  const hydrateSectorDrafts = (limits: SectorLimits) => {
    setSectorLimitsState(limits);
    setSectorDefaultDraft(String(limits.defaultCapPct));
    setSectorDrafts(
      Object.fromEntries(
        limits.sectors.map((sector) => [sector.sector, String(sector.capPct)]),
      ),
    );
    setSectorOverrideDrafts(
      Object.fromEntries(
        limits.sectors.map((sector) => [sector.sector, !sector.usesDefault]),
      ),
    );
  };

  useEffect(() => {
    if (!selectedBrokerAccountId) return;
    let cancelled = false;
    setSectorLoading(true);
    setSectorError(null);
    void Promise.all([
      listRiskLimits(selectedBrokerAccountId),
      getSectorLimits(selectedBrokerAccountId),
    ])
      .then(([limits, sectors]) => {
        if (cancelled) return;
        setAccountRiskLimits(limits.length > 0 ? limits : riskLimits);
        hydrateSectorDrafts(sectors);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setSectorError(
          error instanceof Error
            ? error.message
            : "Could not load sector limits.",
        );
      })
      .finally(() => {
        if (!cancelled) setSectorLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBrokerAccountId, listRiskLimits, getSectorLimits, riskLimits]);

  const startTighten = (limit: RiskLimit) => {
    setEditingKey(limit.key);
    setTightenValue(String(limit.value));
    setTightenError(null);
  };

  const submitTighten = (limit: RiskLimit) => {
    const n = Number(tightenValue);
    if (!Number.isFinite(n)) {
      setTightenError("Enter a number.");
      return;
    }
    if (n >= limit.value) {
      setTightenError(
        `Tightening means a value below ${limit.value}${limit.unit === "%" ? "%" : ""}. Use loosen ▲ to raise.`,
      );
      return;
    }
    onTighten(limit.key, n);
    setEditingKey(null);
    setTightenError(null);
  };

  const renderUnit = (l: RiskLimit) =>
    l.unit === "%" ? "%" : "";

  const selectedAccount = selectableAccounts.find(
    (account) => account.brokerAccountId === selectedBrokerAccountId,
  );
  const sectorNames = sectorLimits?.sectors.map((row) => row.sector) ?? [
    ...STANDARD_GICS_SECTORS,
  ];

  const changeDefaultCap = (next: string) => {
    setSectorDefaultDraft(next);
    setSectorDrafts((prev) => (
      Object.fromEntries(
        sectorNames.map((sector) => [
          sector,
          sectorOverrideDrafts[sector] ? (prev[sector] ?? next) : next,
        ]),
      )
    ));
  };

  const editSectorCap = (sector: string, next: string) => {
    setSectorDrafts((prev) => ({ ...prev, [sector]: next }));
    setSectorOverrideDrafts((prev) => ({ ...prev, [sector]: true }));
  };

  const resetSectorToDefault = (sector: string) => {
    setSectorDrafts((prev) => ({ ...prev, [sector]: sectorDefaultDraft }));
    setSectorOverrideDrafts((prev) => ({ ...prev, [sector]: false }));
  };

  const clearSectorOverrides = () => {
    setSectorDrafts(
      Object.fromEntries(
        sectorNames.map((sector) => [sector, sectorDefaultDraft]),
      ),
    );
    setSectorOverrideDrafts(
      Object.fromEntries(sectorNames.map((sector) => [sector, false])),
    );
  };

  const saveSectorLimits = async () => {
    if (!selectedBrokerAccountId) return;
    if (!pctDraftIsValid(sectorDefaultDraft)) {
      setSectorError("Default cap must be greater than 0 and no more than 100.");
      return;
    }
    const overrides: Record<string, string | null> = {};
    for (const sector of sectorNames) {
      if (sectorOverrideDrafts[sector]) {
        const draft = sectorDrafts[sector] ?? "";
        if (!pctDraftIsValid(draft)) {
          setSectorError(`${sector} cap must be greater than 0 and no more than 100.`);
          return;
        }
        overrides[sector] = draft;
      } else {
        overrides[sector] = null;
      }
    }
    setSectorSaving(true);
    setSectorError(null);
    try {
      const updated = await setSectorLimits({
        brokerAccountId: selectedBrokerAccountId,
        defaultCapPct: sectorDefaultDraft,
        overrides,
      });
      hydrateSectorDrafts(updated);
      const limits = await listRiskLimits(selectedBrokerAccountId);
      setAccountRiskLimits(limits.length > 0 ? limits : riskLimits);
    } catch (error) {
      setSectorError(
        error instanceof Error
          ? error.message
          : "Could not save sector limits.",
      );
    } finally {
      setSectorSaving(false);
    }
  };

  const saveCoolingOffDuration = async () => {
    const next = Number(coolingOffDraft);
    if (
      !Number.isInteger(next) ||
      next < 1 ||
      next > 30
    ) {
      setCoolingOffError("Enter a whole number from 1 to 30.");
      return;
    }
    setCoolingOffSaving(true);
    setCoolingOffError(null);
    try {
      const updated = await apiClient.setCoolingOffConfig({
        cooling_off_days: next,
      });
      onCoolingOffDaysChange(updated.cooling_off_days);
      setCoolingOffDraft(String(updated.cooling_off_days));
      setCoolingOffEditing(false);
    } catch (error) {
      setCoolingOffError(
        error instanceof Error
          ? error.message
          : "Could not save cooling-off duration.",
      );
    } finally {
      setCoolingOffSaving(false);
    }
  };

  return (
    <div className="tp-tile">
      <div className="tp-tile-head">
        <span className="label">Risk limits</span>
        <div className="tp-risk-account-select">
          <label htmlFor="risk-account-select">Account</label>
          <select
            id="risk-account-select"
            className="tp-input"
            value={selectedBrokerAccountId}
            onChange={(event) => setSelectedBrokerAccountId(event.target.value)}
            disabled={selectableAccounts.length === 0}
          >
            {selectableAccounts.map((account) => (
              <option
                key={account.brokerAccountId}
                value={account.brokerAccountId}
              >
                {account.label} · {account.broker}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="tp-tile-body">
        <div className="tp-risk-rows">
          {accountRiskLimits.map((limit) => {
            const isSectorLimit = limit.key === "sector_concentration";
            const isEditing = editingKey === limit.key;
            return (
              <Fragment key={limit.key}>
                <div className="tp-risk-row">
                  <div className="lbl">{limit.label}</div>
                  {isEditing ? (
                    <div className="ctrl-edit">
                      <input
                        type="number"
                        step={limit.unit === "%" ? "0.1" : "1"}
                        className="tp-input num"
                        value={tightenValue}
                        onChange={(e) => {
                          setTightenValue(e.target.value);
                          setTightenError(null);
                        }}
                        aria-label={`Tighten ${limit.label} new value`}
                      />
                      <button
                        type="button"
                        className="tp-btn tp-btn-primary tp-btn-sm"
                        onClick={() => submitTighten(limit)}
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        className="tp-btn tp-btn-secondary tp-btn-sm"
                        onClick={() => {
                          setEditingKey(null);
                          setTightenError(null);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="value num">
                        {limit.value}
                        {renderUnit(limit)}
                      </div>
                      <div className="actions">
                        {isSectorLimit ? (
                          <button
                            type="button"
                            className="tp-btn tp-btn-secondary tp-btn-sm"
                            onClick={() => setSectorExpanded((open) => !open)}
                            disabled={!selectedBrokerAccountId}
                            aria-expanded={sectorExpanded}
                          >
                            {sectorExpanded ? "collapse" : "configure"}
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="tp-btn tp-btn-secondary tp-btn-sm"
                              onClick={() => startTighten(limit)}
                              aria-label={`Tighten ${limit.label}`}
                            >
                              tighten ▼
                            </button>
                            <button
                              type="button"
                              className="tp-btn tp-btn-secondary tp-btn-sm"
                              onClick={() => onOpenLoosen(limit.key)}
                              aria-label={`Loosen ${limit.label}`}
                            >
                              loosen ▲
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                  {isEditing && tightenError && (
                    <div className="row-err">{tightenError}</div>
                  )}
                </div>
                {isSectorLimit && sectorExpanded && (
                  <div className="tp-sector-panel">
                    <div className="tp-sector-panel-head">
                      <div>
                        <div className="hdr">Sector concentration</div>
                        <div className="sub">
                          {selectedAccount
                            ? `${selectedAccount.label} · ${selectedAccount.broker}`
                            : "No account selected"}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="tp-btn tp-btn-secondary tp-btn-sm"
                        onClick={clearSectorOverrides}
                        disabled={sectorLoading || sectorSaving}
                      >
                        Use default set
                      </button>
                    </div>

                    <div className="tp-sector-default-row">
                      <label htmlFor="sector-default-cap">Default cap</label>
                      <input
                        id="sector-default-cap"
                        type="number"
                        min="0.1"
                        max="100"
                        step="0.1"
                        className="tp-input num"
                        value={sectorDefaultDraft}
                        onChange={(event) => changeDefaultCap(event.target.value)}
                        disabled={sectorLoading || sectorSaving}
                      />
                      <span className="unit">%</span>
                    </div>

                    {sectorError && (
                      <div className="tp-sector-error">{sectorError}</div>
                    )}
                    {sectorLoading ? (
                      <div className="tp-sector-loading">Loading limits...</div>
                    ) : (
                      <div className="tp-sector-grid">
                        {sectorNames.map((sector) => {
                          const isOverride = sectorOverrideDrafts[sector] ?? false;
                          return (
                            <div className="tp-sector-row" key={sector}>
                              <div className="sector-name">
                                <span>{sector}</span>
                                <span className={isOverride ? "state override" : "state"}>
                                  {isOverride ? "Override" : "Default"}
                                </span>
                              </div>
                              <div className="sector-input">
                                <input
                                  type="number"
                                  min="0.1"
                                  max="100"
                                  step="0.1"
                                  className="tp-input num"
                                  value={sectorDrafts[sector] ?? sectorDefaultDraft}
                                  onChange={(event) => (
                                    editSectorCap(sector, event.target.value)
                                  )}
                                  disabled={sectorSaving}
                                  aria-label={`${sector} sector cap`}
                                />
                                <span className="unit">%</span>
                              </div>
                              <button
                                type="button"
                                className="tp-btn tp-btn-secondary tp-btn-sm"
                                onClick={() => resetSectorToDefault(sector)}
                                disabled={!isOverride || sectorSaving}
                              >
                                Use default
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="tp-sector-actions">
                      <button
                        type="button"
                        className="tp-btn tp-btn-primary tp-btn-sm"
                        onClick={saveSectorLimits}
                        disabled={
                          sectorLoading ||
                          sectorSaving ||
                          !selectedBrokerAccountId
                        }
                      >
                        {sectorSaving ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            );
          })}

          <div className="tp-risk-row">
            <div className="lbl">Cooling-off duration</div>
            {coolingOffEditing ? (
              <div className="ctrl-edit">
                <input
                  type="number"
                  min="1"
                  max="30"
                  step="1"
                  className="tp-input num"
                  value={coolingOffDraft}
                  onChange={(event) => {
                    setCoolingOffDraft(event.target.value);
                    setCoolingOffError(null);
                  }}
                  aria-label="Cooling-off duration days"
                />
                <button
                  type="button"
                  className="tp-btn tp-btn-primary tp-btn-sm"
                  onClick={saveCoolingOffDuration}
                  disabled={coolingOffSaving}
                >
                  {coolingOffSaving ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className="tp-btn tp-btn-secondary tp-btn-sm"
                  onClick={() => {
                    setCoolingOffDraft(String(coolingOffDays));
                    setCoolingOffEditing(false);
                    setCoolingOffError(null);
                  }}
                  disabled={coolingOffSaving}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <div className="value num">
                  {coolingOffDays} day{coolingOffDays === 1 ? "" : "s"}
                </div>
                <div className="actions">
                  <button
                    type="button"
                    className="tp-btn tp-btn-secondary tp-btn-sm"
                    onClick={() => setCoolingOffEditing(true)}
                    aria-label="Edit cooling-off duration"
                  >
                    edit
                  </button>
                </div>
              </>
            )}
            {coolingOffError && (
              <div className="row-err">{coolingOffError}</div>
            )}
          </div>
        </div>

        {pendingChanges.length > 0 && (
          <div className="tp-pending-changes">
            <div className="hdr">
              Pending changes ({pendingChanges.length})
            </div>
            {pendingChanges.map((p) => (
              <div className="row" key={p.id}>
                <div className="kv">
                  <span className="k">{p.label}:</span>{" "}
                  <span className="num">
                    {p.currentValue}
                    {p.unit === "%" ? "%" : ""} →{" "}
                  </span>
                  <span className="num pos">
                    {p.newValue}
                    {p.unit === "%" ? "%" : ""}
                  </span>
                </div>
                <div className="kv">
                  <span className="k">Activates:</span>{" "}
                  <span className="num">
                    {p.activatesAtDisplay} ({relativeUntil(p.activatesAtMs)})
                  </span>
                </div>
                <div className="kv reflection">
                  <span className="k">Reflection:</span> "{p.reflection}"
                </div>
                <button
                  type="button"
                  className="tp-btn tp-btn-danger tp-btn-sm"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Cancel this pending change? The change will not activate. You can re-submit later.",
                      )
                    ) {
                      onCancelPending(p.id);
                    }
                  }}
                >
                  cancel pending change
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

type AccountsTabProps = {
  accounts: Account[];
  positions: Position[];
  apiClient: ApiClient;
};

const AccountsTab = ({ accounts, positions, apiClient }: AccountsTabProps) => {
  // Group exposure strictly per broker account so paper and live books
  // are never summed into one blended figure. Each account keeps its own
  // open-position count and gross market value.
  const exposureByAccount = useMemo(() => {
    const labelFor = new Map(
      accounts.map((a) => [a.brokerAccountId, a.label]),
    );
    const groups = new Map<
      string,
      { brokerAccountId: string; label: string; openCount: number; grossMarketValue: number }
    >();
    for (const p of positions) {
      const id = p.brokerAccountId;
      const existing = groups.get(id) ?? {
        brokerAccountId: id,
        label: labelFor.get(id) ?? id,
        openCount: 0,
        grossMarketValue: 0,
      };
      existing.openCount += 1;
      existing.grossMarketValue += p.last * p.qty;
      groups.set(id, existing);
    }
    return Array.from(groups.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [accounts, positions]);

  const [credentialValues, setCredentialValues] = useState<
    Partial<Record<keyof WizardCredentialsRequest, string>>
  >({ coingecko_api_plan: "demo" });
  const [credentialsSaving, setCredentialsSaving] = useState<boolean>(false);
  const [credentialsMessage, setCredentialsMessage] = useState<string | null>(
    null,
  );
  const [credentialsError, setCredentialsError] = useState<string | null>(null);

  // Alpaca paper credential save.
  const [alpacaPaperSaving, setAlpacaPaperSaving] = useState<boolean>(false);
  const [alpacaPaperMessage, setAlpacaPaperMessage] = useState<string | null>(
    null,
  );
  const [alpacaPaperError, setAlpacaPaperError] = useState<string | null>(null);

  // Alpaca LIVE credential save — gated by an explicit confirmation
  // checkbox so a stray paste cannot silently write real-money keys.
  const [liveCredsConfirmed, setLiveCredsConfirmed] = useState<boolean>(false);
  const [alpacaLiveSaving, setAlpacaLiveSaving] = useState<boolean>(false);
  const [alpacaLiveMessage, setAlpacaLiveMessage] = useState<string | null>(
    null,
  );
  const [alpacaLiveError, setAlpacaLiveError] = useState<string | null>(null);

  // Live trading-account provisioning (one-step "Test & provision").
  const [liveDisplayName, setLiveDisplayName] = useState<string>("");
  const [provisionSaving, setProvisionSaving] = useState<boolean>(false);
  const [provisionMessage, setProvisionMessage] = useState<string | null>(null);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  // Provisioned-but-dark live accounts (DB-sourced, independent of the
  // registry/gates) shown as "pending authorization" cards.
  const [pendingLive, setPendingLive] = useState<PendingLiveAccount[]>([]);

  const refreshPendingLive = useCallback(async () => {
    try {
      const result = await apiClient.listPendingLiveAccounts();
      setPendingLive(result.accounts);
    } catch {
      // Non-fatal: the pending-live card simply hides if the read fails.
      setPendingLive([]);
    }
  }, [apiClient]);

  useEffect(() => {
    void refreshPendingLive();
  }, [refreshPendingLive]);

  const saveCredentialFields = async (
    fields: CredentialField[],
    setSaving: (v: boolean) => void,
    setMessage: (v: string | null) => void,
    setError: (v: string | null) => void,
    label: string,
  ) => {
    const payload: WizardCredentialsRequest = {};
    for (const field of fields) {
      const value = credentialValues[field.key]?.trim();
      if (value) {
        payload[field.key] = value;
      }
    }
    if (Object.keys(payload).length === 0) {
      setError("Enter at least one value.");
      setMessage(null);
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const result = await apiClient.postWizardCredentials(payload);
      setMessage(
        result.updated.length > 0
          ? `Updated ${result.updated.join(", ")}`
          : "No values changed",
      );
      // Never leave secrets in component state after a save.
      setCredentialValues((curr) => {
        const next = { ...curr };
        for (const field of fields) {
          delete next[field.key];
        }
        return next;
      });
    } catch (e) {
      setError(problemMessage(e, `Failed to save ${label}`));
    } finally {
      setSaving(false);
    }
  };

  const saveAlpacaPaperCredentials = () =>
    saveCredentialFields(
      ALPACA_PAPER_CREDENTIAL_FIELDS,
      setAlpacaPaperSaving,
      setAlpacaPaperMessage,
      setAlpacaPaperError,
      "Alpaca paper keys",
    );

  const saveAlpacaLiveCredentials = async () => {
    if (!liveCredsConfirmed) {
      setAlpacaLiveError(
        "Confirm you understand these are live real-money credentials.",
      );
      return;
    }
    await saveCredentialFields(
      ALPACA_LIVE_CREDENTIAL_FIELDS,
      setAlpacaLiveSaving,
      setAlpacaLiveMessage,
      setAlpacaLiveError,
      "Alpaca LIVE keys",
    );
    // Re-arm the confirmation gate after each save.
    setLiveCredsConfirmed(false);
  };

  const testAndProvisionLiveAccount = async () => {
    const name = liveDisplayName.trim();
    if (!name) {
      setProvisionError("Enter a display name for the live account.");
      setProvisionMessage(null);
      return;
    }
    setProvisionSaving(true);
    setProvisionError(null);
    setProvisionMessage(null);
    try {
      const result = await apiClient.postTestAndProvisionLiveAccount({
        display_name: name,
      });
      const provisionedNote = result.created
        ? "provisioned (pending authorization)"
        : "already provisioned (pending authorization)";
      setProvisionMessage(
        `Connected to live Alpaca · account ••••${result.account_number_last4} ` +
          `· equity $${result.equity} · ${provisionedNote}.`,
      );
      // Refresh the pending-live cards so the new dark account appears.
      await refreshPendingLive();
    } catch (e) {
      // The RFC 7807 detail (e.g. "save live credentials first",
      // "live credentials rejected by Alpaca") surfaces verbatim.
      setProvisionError(
        problemMessage(e, "Failed to test and provision live account"),
      );
    } finally {
      setProvisionSaving(false);
    }
  };

  const saveAccountCredentials = async () => {
    const payload: WizardCredentialsRequest = {};
    for (const field of ACCOUNT_CREDENTIAL_FIELDS) {
      const value = credentialValues[field.key]?.trim();
      if (value) {
        payload[field.key] = value;
      }
    }
    const plan = credentialValues.coingecko_api_plan ?? "demo";
    if (plan === "demo" || plan === "pro") {
      payload.coingecko_api_plan = plan;
    }
    if (Object.keys(payload).length === 0) {
      setCredentialsError("Enter at least one value.");
      setCredentialsMessage(null);
      return;
    }
    setCredentialsSaving(true);
    setCredentialsError(null);
    setCredentialsMessage(null);
    try {
      const result = await apiClient.postWizardCredentials(payload);
      setCredentialsMessage(
        result.updated.length > 0
          ? `Updated ${result.updated.join(", ")}`
          : "No values changed",
      );
      setCredentialValues({ coingecko_api_plan: plan });
    } catch (e) {
      setCredentialsError(problemMessage(e, "Failed to save account keys"));
    } finally {
      setCredentialsSaving(false);
    }
  };

  return (
    <ConfigGrid>
      <ConfigPanel title="Broker accounts">
        {accounts.length === 0 ? (
          <Unavailable
            title="No broker accounts registered"
            detail="The /api/v1/accounts data source is wired. It is returning no registered accounts."
          />
        ) : (
          <div className="tp-config-list">
            {accounts.map((account) => (
              <div className="tp-config-record" key={account.brokerAccountId}>
                <div>
                  <div className="name">{account.label}</div>
                  <div className="meta">
                    {account.broker} {account.mask}
                  </div>
                </div>
                <div className={`state ${account.status}`}>
                  {account.status}
                </div>
                <Metric
                  label={
                    account.balanceSource === "asset_class_sleeve" ||
                    account.balanceSource === "asset_class_exposure"
                      ? "Sleeve equity"
                      : "Equity"
                  }
                  value={money(account.equity)}
                />
                <Metric label="Buying power" value={money(account.buyingPower)} />
                <Metric
                  label={
                    account.balanceSource === "asset_class_sleeve" ||
                    account.balanceSource === "asset_class_exposure"
                      ? "Sleeve P/L"
                      : "Today P/L"
                  }
                  value={money(account.todayPL)}
                />
                {account.balanceSource === "asset_class_sleeve" ||
                account.balanceSource === "asset_class_exposure" ? (
                  <Metric
                    label="Broker equity"
                    value={money(account.brokerEquity ?? account.equity)}
                  />
                ) : null}
              </div>
            ))}
          </div>
        )}
      </ConfigPanel>
      <ConfigPanel title="Account exposure">
        {exposureByAccount.length === 0 ? (
          <Metric label="Open positions" value="0" />
        ) : (
          exposureByAccount.map((group) => (
            <div
              key={group.brokerAccountId}
              data-testid={`exposure-${group.brokerAccountId}`}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--tp-fg-3)",
                  marginTop: 4,
                }}
              >
                {group.label}
              </div>
              <Metric
                label="Open positions"
                value={String(group.openCount)}
              />
              <Metric
                label="Gross market value"
                value={money(group.grossMarketValue)}
              />
            </div>
          ))
        )}
        <Unavailable
          title="Some account controls unavailable"
          detail="Paper/live routing per strategy is available in the Strategy routes panel below. Strategy registration and full-size live promotion (after the reduced-size period) do not have a UI-safe mutation endpoint yet."
        />
      </ConfigPanel>
      <ConfigPanel title="Market data credentials">
        <div className="tp-crypto-form-grid">
          {ACCOUNT_CREDENTIAL_FIELDS.map((field) => (
            <ConfigField
              key={field.key}
              label={field.label}
              type={field.secret ? "password" : "text"}
              value={credentialValues[field.key] ?? ""}
              onChange={(value) =>
                setCredentialValues((curr) => ({
                  ...curr,
                  [field.key]: value,
                }))
              }
            />
          ))}
          <label className="tp-crypto-field">
            CoinGecko plan
            <select
              className="tp-input"
              value={credentialValues.coingecko_api_plan ?? "demo"}
              onChange={(e) =>
                setCredentialValues((curr) => ({
                  ...curr,
                  coingecko_api_plan: e.target.value,
                }))
              }
            >
              <option value="demo">Demo/public</option>
              <option value="pro">Pro</option>
            </select>
          </label>
        </div>
        <div className="tp-crypto-actions">
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={() => void saveAccountCredentials()}
            disabled={credentialsSaving}
          >
            Save market data keys
          </button>
          {credentialsMessage && (
            <span className="tp-inline-ok">{credentialsMessage}</span>
          )}
          {credentialsError && (
            <span className="tp-inline-error">{credentialsError}</span>
          )}
        </div>
      </ConfigPanel>
      <ConfigPanel title="Alpaca paper credentials">
        <p className="tp-config-help">
          Paper-trading keys. Saving stores them in the OS keychain and
          rebinds the running paper broker adapter immediately.
        </p>
        <div className="tp-crypto-form-grid">
          {ALPACA_PAPER_CREDENTIAL_FIELDS.map((field) => (
            <ConfigField
              key={field.key}
              label={field.label}
              type={field.secret ? "password" : "text"}
              value={credentialValues[field.key] ?? ""}
              onChange={(value) =>
                setCredentialValues((curr) => ({
                  ...curr,
                  [field.key]: value,
                }))
              }
            />
          ))}
        </div>
        <div className="tp-crypto-actions">
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={() => void saveAlpacaPaperCredentials()}
            disabled={alpacaPaperSaving}
          >
            Save Alpaca paper keys
          </button>
          {alpacaPaperMessage && (
            <span className="tp-inline-ok">{alpacaPaperMessage}</span>
          )}
          {alpacaPaperError && (
            <span className="tp-inline-error">{alpacaPaperError}</span>
          )}
        </div>
      </ConfigPanel>
      <ConfigPanel title="Alpaca LIVE credentials — real money">
        <div className="tp-ack danger" style={{ marginBottom: 12 }}>
          <label>
            These are REAL-MONEY live credentials. Saving them stores the
            keys in the OS keychain but does NOT enable live trading — live
            order placement stays disabled until the /before-live-trade
            authorization ritual is completed.
          </label>
        </div>
        <div className="tp-crypto-form-grid">
          {ALPACA_LIVE_CREDENTIAL_FIELDS.map((field) => (
            <ConfigField
              key={field.key}
              label={field.label}
              type="password"
              value={credentialValues[field.key] ?? ""}
              onChange={(value) =>
                setCredentialValues((curr) => ({
                  ...curr,
                  [field.key]: value,
                }))
              }
            />
          ))}
        </div>
        <div className="tp-ack danger" style={{ marginTop: 4 }}>
          <input
            type="checkbox"
            id="tp-live-creds-confirm"
            checked={liveCredsConfirmed}
            onChange={(e) => setLiveCredsConfirmed(e.target.checked)}
          />
          <label htmlFor="tp-live-creds-confirm">
            I understand these are live real-money credentials.
          </label>
        </div>
        <div className="tp-crypto-actions">
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={() => void saveAlpacaLiveCredentials()}
            disabled={alpacaLiveSaving || !liveCredsConfirmed}
          >
            Save Alpaca LIVE keys
          </button>
          {alpacaLiveMessage && (
            <span className="tp-inline-ok">{alpacaLiveMessage}</span>
          )}
          {alpacaLiveError && (
            <span className="tp-inline-error">{alpacaLiveError}</span>
          )}
        </div>
      </ConfigPanel>
      <ConfigPanel title="Test & provision live account">
        <p className="tp-config-help">
          Validates your saved live Alpaca keys with a read-only connection
          test, fetches the account, and provisions the live trading-account
          row in one step — no need to type the account number. The account
          stays dark — no live orders can be placed — until the
          /before-live-trade ritual flips the live-authorization gates. This
          control never touches those gates and never places an order.
        </p>
        <div className="tp-crypto-form-grid">
          <ConfigField
            label="Display name"
            value={liveDisplayName}
            onChange={setLiveDisplayName}
          />
        </div>
        <div className="tp-crypto-actions">
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={() => void testAndProvisionLiveAccount()}
            disabled={provisionSaving}
          >
            {provisionSaving ? "Testing…" : "Test & provision live account"}
          </button>
          {provisionMessage && (
            <span className="tp-inline-ok">{provisionMessage}</span>
          )}
          {provisionError && (
            <span className="tp-inline-error">{provisionError}</span>
          )}
        </div>
        {pendingLive.length > 0 && (
          <div className="tp-pending-live-list">
            {pendingLive.map((acct) => (
              <div
                key={acct.broker_account_id}
                className="tp-pending-live-card"
                data-testid="pending-live-card"
              >
                <div className="tp-pending-live-head">
                  <span className="tp-pending-live-name">
                    {acct.display_name}
                  </span>
                  <span className="tp-pending-live-badge">
                    {acct.authorized
                      ? "Authorized — active"
                      : "Provisioned — pending /before-live-trade authorization · not trading"}
                  </span>
                </div>
                <div className="tp-pending-live-meta">
                  {acct.broker} · {acct.asset_class} · account ••••{acct.mask}
                </div>
                {!acct.authorized && (
                  <p className="tp-pending-live-note">
                    This account becomes active only after the
                    /before-live-trade ritual is completed and signed off. It
                    is not trading and places no orders.
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </ConfigPanel>
    </ConfigGrid>
  );
};

const DataTab = ({
  apiClient,
  systemHealth,
  recentEvents,
}: {
  apiClient: ApiClient;
  systemHealth: SystemHealthRow[];
  recentEvents: EventRow[];
}) => {
  const fixturesActive = useFixtures();
  const dataRows = systemHealth.filter((row) =>
    ["data_feed", "database", "overall"].includes(row.key),
  );
  const [exchangeCapabilities, setExchangeCapabilities] =
    useState<CryptoExchangeCapabilityListResponseWire | null>(
      fixturesActive ? FIXTURE_CRYPTO_EXCHANGE_CAPABILITIES : null,
    );
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  // Phase 9-UI: read-only forex gate view for the rollout panel.
  const [forexConfig, setForexConfig] = useState<ForexConfigWire | null>(
    fixturesActive
      ? {
          forex_trading_enabled: false,
          forex_trading_enabled_at: null,
          forex_trading_enabled_by: null,
        }
      : null,
  );
  const [forexConfigError, setForexConfigError] = useState<string | null>(
    null,
  );
  // Phase 9 setup: tastyfx demo-account registration + credentials.
  const [forexAccount, setForexAccount] = useState<ForexAccountWire | null>(
    fixturesActive ? { registered: false } : null,
  );
  const [forexAccountError, setForexAccountError] = useState<string | null>(
    null,
  );
  const [fxCredValues, setFxCredValues] = useState<Record<string, string>>(
    {},
  );
  const [fxCredSaving, setFxCredSaving] = useState(false);
  const [fxCredMessage, setFxCredMessage] = useState<string | null>(null);
  const [fxCredError, setFxCredError] = useState<string | null>(null);
  const [fxRegDisplayName, setFxRegDisplayName] = useState("tastyfx demo");
  const [fxRegExternalId, setFxRegExternalId] = useState("");
  const [fxRegistering, setFxRegistering] = useState(false);

  useEffect(() => {
    if (fixturesActive) {
      setExchangeCapabilities(FIXTURE_CRYPTO_EXCHANGE_CAPABILITIES);
      setExchangeError(null);
      return;
    }
    let cancelled = false;
    apiClient
      .getCryptoExchangeCapabilities()
      .then((body) => {
        if (!cancelled) {
          setExchangeCapabilities(body);
          setExchangeError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setExchangeError(problemMessage(e, "Failed to load crypto exchange policy"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, fixturesActive]);

  useEffect(() => {
    if (fixturesActive) return;
    let cancelled = false;
    apiClient
      .getForexConfig()
      .then((body) => {
        if (!cancelled) {
          setForexConfig(body);
          setForexConfigError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setForexConfigError(
            problemMessage(e, "Failed to load forex gate state"),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, fixturesActive]);

  useEffect(() => {
    if (fixturesActive) return;
    let cancelled = false;
    apiClient
      .getForexAccount()
      .then((body) => {
        if (!cancelled) {
          setForexAccount(body);
          setForexAccountError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setForexAccountError(
            problemMessage(e, "Failed to load forex account state"),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, fixturesActive]);

  const saveTastyfxCredentials = async () => {
    const payload: WizardCredentialsRequest = {};
    for (const field of [
      ...TASTYFX_DEMO_CREDENTIAL_FIELDS,
      ...TASTYFX_LIVE_CREDENTIAL_FIELDS,
    ]) {
      const value = fxCredValues[field.key]?.trim();
      if (value) {
        payload[field.key] = value;
      }
    }
    if (Object.keys(payload).length === 0) {
      setFxCredError("Enter at least one value.");
      setFxCredMessage(null);
      return;
    }
    setFxCredSaving(true);
    setFxCredError(null);
    setFxCredMessage(null);
    try {
      const result = await apiClient.postWizardCredentials(payload);
      setFxCredMessage(
        result.updated.length > 0
          ? `Stored in keychain: ${result.updated.join(", ")}`
          : "No values changed",
      );
      setFxCredValues({});
    } catch (e) {
      setFxCredError(problemMessage(e, "Failed to save tastyfx credentials"));
    } finally {
      setFxCredSaving(false);
    }
  };

  const registerTastyfxDemo = async () => {
    setFxRegistering(true);
    setForexAccountError(null);
    try {
      const result = await apiClient.registerForexAccount({
        broker_account_external_id: fxRegExternalId.trim(),
        display_name: fxRegDisplayName.trim(),
      });
      setForexAccount(result);
    } catch (e) {
      setForexAccountError(
        problemMessage(e, "Failed to register the tastyfx demo account"),
      );
    } finally {
      setFxRegistering(false);
    }
  };

  return (
    <ConfigGrid>
      <ConfigPanel title="Data health">
        <HealthRows rows={dataRows.length > 0 ? dataRows : systemHealth} />
      </ConfigPanel>
      <ConfigPanel title="Recent data events">
        <EventList events={filterEvents(recentEvents, ["data", "ingest", "ohlcv"])} />
        <Unavailable
          title="Provider settings unavailable"
          detail="Polygon, PIT membership, and news provider credentials are captured by the wizard/keychain path, but no dedicated read/edit endpoint exists for provider configuration."
        />
      </ConfigPanel>
      <ConfigPanel title="Crypto exchange rollout">
        {exchangeError && (
          <div className="tp-inline-status bad">{exchangeError}</div>
        )}
        {exchangeCapabilities === null && exchangeError === null ? (
          <div className="tp-muted">Loading exchange policy...</div>
        ) : (
          <div className="tp-config-list">
            {(exchangeCapabilities?.exchanges ?? []).slice(0, 14).map((row) => (
              <div className="tp-config-record" key={row.exchange_id}>
                <div>
                  <div className="name">{row.display_name}</div>
                  <div className="meta">
                    {row.spot_supported_by_freqtrade ? "Freqtrade spot" : "TradePro native"}
                    {row.stoploss_on_exchange
                      ? ` · stop ${row.stoploss_on_exchange}`
                      : " · no exchange stop"}
                  </div>
                  {row.notes.length > 0 && (
                    <div className="meta">{row.notes[0]}</div>
                  )}
                </div>
                <div className={`state ${row.live_trading_enabled ? "ok" : "idle"}`}>
                  {row.tradepro_stage.replaceAll("_", " ")}
                </div>
                <Metric
                  label="Live"
                  value={row.live_trading_enabled ? "enabled" : "gated"}
                />
                <Metric
                  label="Adapter"
                  value={row.broker_adapter ?? "not built"}
                />
              </div>
            ))}
          </div>
        )}
      </ConfigPanel>
      <ConfigPanel title="Forex rollout">
        {forexConfigError && (
          <div className="tp-inline-status bad">{forexConfigError}</div>
        )}
        <div className="tp-config-list">
          <div className="tp-config-record">
            <div>
              <div className="name">Trading gate</div>
              <div className="meta">
                system_config.forex_trading_enabled — Override 5 Tier A.
                Read-only here by design: enabling requires Phase 9e
                paper-trade validation and a manual operator UPDATE, the
                same posture as the live-trading gates.
              </div>
              {forexConfig?.forex_trading_enabled &&
                forexConfig.forex_trading_enabled_by && (
                  <div className="meta">
                    enabled {forexConfig.forex_trading_enabled_at ?? ""} by{" "}
                    {forexConfig.forex_trading_enabled_by}
                  </div>
                )}
            </div>
            <div
              className={`state ${forexConfig?.forex_trading_enabled ? "ok" : "idle"}`}
            >
              {forexConfig === null
                ? "unknown"
                : forexConfig.forex_trading_enabled
                  ? "enabled"
                  : "disabled"}
            </div>
            <Metric
              label="Gate"
              value={
                forexConfig?.forex_trading_enabled ? "enabled" : "gated"
              }
            />
            <Metric label="Adapter" value="not built" />
          </div>
          <div className="tp-config-record">
            <div>
              <div className="name">tastyfx</div>
              <div className="meta">
                Primary forex broker (IG US — operator account exists;
                OANDA can be added later as a second venue). Broker
                adapter, 24/5 calendar/scheduler, and the margin/leverage
                model arrive with Phase 9b; strategies with Phase 9c.
                Data entitlement (Polygon C: aggs) is confirmed against
                the live API.
              </div>
            </div>
            <div className="state idle">phase 9b</div>
            <Metric label="Data" value="confirmed" />
            <Metric label="Strategies" value="phase 9c" />
          </div>
        </div>
      </ConfigPanel>
      <ConfigPanel title="Forex broker setup (tastyfx)">
        <p className="tp-config-hint">
          Store tastyfx (IG US) credentials in the OS keychain and
          register the demo account ahead of the Phase 9b adapter.
          Registration inserts a DISABLED broker_accounts row —
          invisible to the broker registry, so nothing can trade from
          it. Live credentials are stored for later; live forex trading
          additionally requires a separate signed Tier B override.
        </p>
        {forexAccountError && (
          <div className="tp-inline-status bad">{forexAccountError}</div>
        )}
        <div className="tp-config-list">
          <div className="tp-config-record">
            <div>
              <div className="name">Demo account registration</div>
              <div className="meta">
                {forexAccount?.registered
                  ? `${forexAccount.display_name ?? "tastyfx demo"} · ` +
                    `···${forexAccount.external_id_last4 ?? ""} · ` +
                    "dormant until the Phase 9b adapter"
                  : "No tastyfx account row registered yet."}
              </div>
            </div>
            <div
              className={`state ${forexAccount?.registered ? "ok" : "idle"}`}
            >
              {forexAccount === null
                ? "unknown"
                : forexAccount.registered
                  ? "registered"
                  : "not registered"}
            </div>
            <Metric
              label="Enabled"
              value={
                forexAccount?.registered
                  ? forexAccount.enabled
                    ? "yes"
                    : "no (dormant)"
                  : "—"
              }
            />
          </div>
        </div>
        {forexAccount !== null && !forexAccount.registered && (
          <>
            <div className="tp-crypto-form-grid">
              <ConfigField
                label="Display name"
                value={fxRegDisplayName}
                onChange={setFxRegDisplayName}
              />
              <ConfigField
                label="Demo account identifier"
                value={fxRegExternalId}
                onChange={setFxRegExternalId}
              />
            </div>
            <div className="tp-crypto-actions">
              <button
                type="button"
                className="tp-btn tp-btn-primary"
                onClick={() => void registerTastyfxDemo()}
                disabled={
                  fxRegistering ||
                  fxRegDisplayName.trim() === "" ||
                  fxRegExternalId.trim() === ""
                }
              >
                {fxRegistering
                  ? "Registering..."
                  : "Register demo account (stays disabled)"}
              </button>
            </div>
          </>
        )}
        <div className="tp-crypto-form-grid">
          {[
            ...TASTYFX_DEMO_CREDENTIAL_FIELDS,
            ...TASTYFX_LIVE_CREDENTIAL_FIELDS,
          ].map((field) => (
            <ConfigField
              key={field.key}
              label={field.label}
              type={field.secret ? "password" : "text"}
              value={fxCredValues[field.key] ?? ""}
              onChange={(value) =>
                setFxCredValues((curr) => ({
                  ...curr,
                  [field.key]: value,
                }))
              }
            />
          ))}
        </div>
        <div className="tp-crypto-actions">
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={() => void saveTastyfxCredentials()}
            disabled={fxCredSaving}
          >
            Save tastyfx credentials
          </button>
          {fxCredMessage && (
            <span className="tp-inline-ok">{fxCredMessage}</span>
          )}
          {fxCredError && (
            <span className="tp-inline-error">{fxCredError}</span>
          )}
        </div>
      </ConfigPanel>
    </ConfigGrid>
  );
};

type StringCredentialKey = Exclude<
  keyof WizardCredentialsRequest,
  "coingecko_api_plan"
>;

type CredentialField = {
  key: StringCredentialKey;
  label: string;
  secret?: boolean;
};

const ACCOUNT_CREDENTIAL_FIELDS: CredentialField[] = [
  { key: "coingecko_api_id", label: "CoinGecko API ID" },
  { key: "coingecko_api_key", label: "CoinGecko API key", secret: true },
];

// Alpaca PAPER broker credentials (safe — paper account only).
const ALPACA_PAPER_CREDENTIAL_FIELDS: CredentialField[] = [
  { key: "alpaca_api_key", label: "Alpaca paper key" },
  { key: "alpaca_api_secret", label: "Alpaca paper secret", secret: true },
];

// Alpaca LIVE broker credentials. REAL-MONEY. Saving them stores the
// keys in the OS keychain but does NOT enable live trading — the
// /before-live-trade ritual gates order placement separately.
const ALPACA_LIVE_CREDENTIAL_FIELDS: CredentialField[] = [
  { key: "alpaca_live_api_key", label: "Alpaca LIVE key", secret: true },
  { key: "alpaca_live_api_secret", label: "Alpaca LIVE secret", secret: true },
];

const CRYPTO_CREDENTIAL_FIELDS: CredentialField[] = [
  { key: "coinbase_api_key", label: "Coinbase paper key" },
  { key: "coinbase_api_secret", label: "Coinbase paper secret", secret: true },
  { key: "glassnode_api_key", label: "Glassnode key", secret: true },
  { key: "coindesk_api_key", label: "Coindesk key", secret: true },
  { key: "cointelegraph_api_key", label: "CoinTelegraph key", secret: true },
];

// tastyfx (IG US) forex broker — DEMO environment. The only
// environment the Phase 9b adapter will service (Override 5 Tier A
// is paper-only). Saving stores keychain entries; it enables nothing.
const TASTYFX_DEMO_CREDENTIAL_FIELDS: CredentialField[] = [
  { key: "tastyfx_username", label: "tastyfx demo username" },
  { key: "tastyfx_password", label: "tastyfx demo password", secret: true },
  { key: "tastyfx_account_id", label: "tastyfx demo account ID" },
];

// tastyfx production credentials. REAL-MONEY. Stored now so setup is
// one sitting; nothing consumes them until a Tier B override (which
// does not yet exist) plus the Phase 9b adapter.
const TASTYFX_LIVE_CREDENTIAL_FIELDS: CredentialField[] = [
  {
    key: "tastyfx_live_username",
    label: "tastyfx LIVE username",
    secret: true,
  },
  {
    key: "tastyfx_live_password",
    label: "tastyfx LIVE password",
    secret: true,
  },
  { key: "tastyfx_live_account_id", label: "tastyfx LIVE account ID" },
];

const CHANNEL_CREDENTIAL_FIELDS: CredentialField[] = [
  { key: "telegram_bot_token", label: "Telegram bot token", secret: true },
  { key: "ntfy_topic", label: "ntfy topic" },
  { key: "ntfy_bearer_token", label: "ntfy bearer token", secret: true },
];

const AI_CREDENTIAL_FIELDS: CredentialField[] = [
  { key: "anthropic_api_key", label: "Anthropic key", secret: true },
  { key: "openai_api_key", label: "OpenAI key", secret: true },
  { key: "google_ai_api_key", label: "Google AI key", secret: true },
];

// Moonshots research-queue enrichment feeds. Absent keys leave rows at
// data_quality "limited" (news/catalysts/disclosures missing) and lower
// their research_priority_score. Display-only ranking; never trades.
const RESEARCH_CREDENTIAL_FIELDS: CredentialField[] = [
  { key: "news_api_key", label: "Benzinga news key", secret: true },
  { key: "finnhub_api_key", label: "Finnhub key (catalysts)", secret: true },
  { key: "quiver_api_key", label: "Quiver key (disclosures)", secret: true },
];

const AI_PROVIDER_OPTIONS = [
  "anthropic",
  "openai",
  "google",
  "ollama",
];

const AI_FEATURE_LABELS: Record<string, string> = {
  signal_scoring: "Signal scoring",
  research_memo: "Research memo",
  long_form_report: "Long-form report",
  multi_agent_review: "Agent review",
  position_reassessment_anthropic: "Position review · Anthropic",
  position_reassessment_codex: "Position review · Codex",
};

const FIXTURE_CRYPTO_EXCHANGE_CAPABILITIES: CryptoExchangeCapabilityListResponseWire = {
  live_enabled_exchange_ids: [],
  recommended_live_rollout: ["coinbase", "kraken"],
  exchanges: [
    {
      exchange_id: "coinbase",
      display_name: "Coinbase Advanced",
      spot_supported_by_freqtrade: false,
      tradepro_stage: "paper_enabled",
      live_trading_enabled: false,
      paper_trading_enabled: true,
      data_adapter: "CoinbaseClient",
      broker_adapter: "CoinbasePaperAdapter / CoinbaseLiveAdapter scaffold",
      stoploss_on_exchange: null,
      requires_passphrase: false,
      notes: ["Primary TradePro crypto venue; live remains validation-gated."],
    },
    {
      exchange_id: "kraken",
      display_name: "Kraken",
      spot_supported_by_freqtrade: true,
      tradepro_stage: "next_candidate",
      live_trading_enabled: false,
      paper_trading_enabled: false,
      data_adapter: null,
      broker_adapter: null,
      stoploss_on_exchange: "market, limit",
      requires_passphrase: false,
      notes: ["Recommended second live venue candidate after Coinbase."],
    },
  ],
};

const TELEGRAM_AGENT_CATEGORIES = [
  "signals",
  "approvals",
  "orders",
  "positions",
  "halts",
  "data",
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const daysAgoIso = (days: number) => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
};

const problemMessage = (e: unknown, fallback: string) => {
  if (e instanceof ProblemDetailError) {
    return e.detail || e.title || fallback;
  }
  return e instanceof Error ? e.message : fallback;
};

const idListToText = (ids: string[]) => ids.join(", ");

const textToIdList = (text: string) =>
  text
    .split(/[,\s]+/)
    .map((value) => value.trim())
    .filter(Boolean);

const yn = (value: boolean | undefined | null) => (value ? "yes" : "no");

const telegramBotLabel = (status: TelegramAgentStatusWire | null) => {
  const bot = status?.bot;
  if (!bot) return status?.bot_api_error ? "error" : "n/a";
  if (bot.username) return `@${bot.username}`;
  return bot.first_name ?? bot.id ?? "n/a";
};

const formatOptionalDateTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString() : "n/a";

const ChannelsTab = ({
  apiClient,
  accounts,
}: {
  apiClient: ApiClient;
  accounts: Account[];
}) => {
  const [credentialValues, setCredentialValues] = useState<
    Partial<Record<keyof WizardCredentialsRequest, string>>
  >({});
  const [credentialsSaving, setCredentialsSaving] = useState<boolean>(false);
  const [credentialsMessage, setCredentialsMessage] = useState<string | null>(
    null,
  );
  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  const [telegramConfig, setTelegramConfig] =
    useState<TelegramAgentConfigWire | null>(null);
  const [telegramDraft, setTelegramDraft] =
    useState<TelegramAgentConfigWire | null>(null);
  const [telegramStatus, setTelegramStatus] =
    useState<TelegramAgentStatusWire | null>(null);
  const [telegramConnectionResult, setTelegramConnectionResult] =
    useState<TelegramAgentTestConnectionResponseWire | null>(null);
  const [telegramChatIdsText, setTelegramChatIdsText] = useState("");
  const [telegramUserIdsText, setTelegramUserIdsText] = useState("");
  const [telegramAccountIdsText, setTelegramAccountIdsText] = useState("");
  const [telegramSaving, setTelegramSaving] = useState<boolean>(false);
  const [telegramConnectionTesting, setTelegramConnectionTesting] =
    useState<boolean>(false);
  const [telegramPipelineTesting, setTelegramPipelineTesting] =
    useState<boolean>(false);
  const [telegramRestarting, setTelegramRestarting] = useState<boolean>(false);
  const [telegramMessage, setTelegramMessage] = useState<string | null>(null);
  const [telegramError, setTelegramError] = useState<string | null>(null);

  const refreshTelegramStatus = async () => {
    try {
      const result = await apiClient.getTelegramAgentStatus();
      setTelegramStatus(result);
      return result;
    } catch (e) {
      setTelegramError(problemMessage(e, "Failed to load Telegram agent status"));
      return null;
    }
  };

  const refreshTelegramConfig = async () => {
    setTelegramError(null);
    try {
      const result = await apiClient.getTelegramAgentConfig();
      setTelegramConfig(result);
      setTelegramDraft(result);
      setTelegramChatIdsText(idListToText(result.allowed_chat_ids));
      setTelegramUserIdsText(idListToText(result.allowed_user_ids));
      setTelegramAccountIdsText(
        idListToText(result.allowed_broker_account_ids ?? []),
      );
      await refreshTelegramStatus();
    } catch (e) {
      setTelegramError(problemMessage(e, "Failed to load Telegram agent config"));
    }
  };

  useEffect(() => {
    void refreshTelegramConfig();
  }, [apiClient]);

  const saveChannelCredentials = async () => {
    const payload: WizardCredentialsRequest = {};
    for (const field of CHANNEL_CREDENTIAL_FIELDS) {
      const value = credentialValues[field.key]?.trim();
      if (value) {
        payload[field.key] = value;
      }
    }
    if (Object.keys(payload).length === 0) {
      setCredentialsError("Enter at least one value.");
      setCredentialsMessage(null);
      return;
    }
    setCredentialsSaving(true);
    setCredentialsError(null);
    setCredentialsMessage(null);
    try {
      const result = await apiClient.postWizardCredentials(payload);
      setCredentialsMessage(
        result.updated.length > 0
          ? `Updated ${result.updated.join(", ")}`
          : "No values changed",
      );
      setCredentialValues({});
      await refreshTelegramStatus();
    } catch (e) {
      setCredentialsError(problemMessage(e, "Failed to save channel keys"));
    } finally {
      setCredentialsSaving(false);
    }
  };

  const saveTelegramConfig = async () => {
    if (telegramDraft === null) return;
    setTelegramSaving(true);
    setTelegramMessage(null);
    setTelegramError(null);
    try {
      const accountIds =
        accounts.length > 0
          ? telegramDraft.allowed_broker_account_ids
          : textToIdList(telegramAccountIdsText);
      const result = await apiClient.setTelegramAgentConfig({
        enabled: telegramDraft.enabled,
        authority_mode: telegramDraft.authority_mode,
        allowed_chat_ids: textToIdList(telegramChatIdsText),
        allowed_user_ids: textToIdList(telegramUserIdsText),
        allowed_broker_account_ids: accountIds,
        notification_categories: telegramDraft.notification_categories,
      });
      setTelegramConfig(result);
      setTelegramDraft(result);
      setTelegramChatIdsText(idListToText(result.allowed_chat_ids));
      setTelegramUserIdsText(idListToText(result.allowed_user_ids));
      setTelegramAccountIdsText(
        idListToText(result.allowed_broker_account_ids ?? []),
      );
      setTelegramMessage("Telegram agent settings saved");
      await refreshTelegramStatus();
    } catch (e) {
      setTelegramError(problemMessage(e, "Failed to save Telegram agent config"));
    } finally {
      setTelegramSaving(false);
    }
  };

  const testTelegramConnection = async () => {
    setTelegramConnectionTesting(true);
    setTelegramMessage(null);
    setTelegramError(null);
    setTelegramConnectionResult(null);
    try {
      const result = await apiClient.testTelegramAgentConnection();
      setTelegramConnectionResult(result);
      setTelegramMessage(
        result.status === "ok"
          ? "Telegram bot connection passed"
          : `Telegram bot connection ${result.status}`,
      );
      await refreshTelegramStatus();
    } catch (e) {
      setTelegramError(problemMessage(e, "Failed to test Telegram bot"));
    } finally {
      setTelegramConnectionTesting(false);
    }
  };

  const testTelegramNotification = async () => {
    setTelegramPipelineTesting(true);
    setTelegramMessage(null);
    setTelegramError(null);
    try {
      const previousDelivery =
        telegramStatus?.telemetry.last_notification_sent_at ?? null;
      await apiClient.testTelegramAgentNotification();
      let nextStatus: TelegramAgentStatusWire | null = null;
      for (let attempt = 0; attempt < 8; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
        nextStatus = await refreshTelegramStatus();
        if (
          nextStatus?.telemetry.last_notification_sent_at &&
          nextStatus.telemetry.last_notification_sent_at !== previousDelivery
        ) {
          break;
        }
      }
      const nextDelivery =
        nextStatus?.telemetry.last_notification_sent_at ?? previousDelivery;
      setTelegramMessage(
        nextDelivery && nextDelivery !== previousDelivery
          ? "Telegram pipeline delivered"
          : "Telegram pipeline queued; delivery not observed yet",
      );
    } catch (e) {
      setTelegramError(problemMessage(e, "Failed to queue Telegram test"));
    } finally {
      setTelegramPipelineTesting(false);
    }
  };

  const restartTelegramAgent = async () => {
    setTelegramRestarting(true);
    setTelegramMessage(null);
    setTelegramError(null);
    try {
      await apiClient.restartTelegramAgent();
      setTelegramMessage("Telegram agent restart requested");
      await refreshTelegramStatus();
    } catch (e) {
      setTelegramError(problemMessage(e, "Failed to restart Telegram agent"));
    } finally {
      setTelegramRestarting(false);
    }
  };

  const setAllowedAccountId = (brokerAccountId: string, enabled: boolean) => {
    setTelegramDraft((curr) => {
      if (curr === null) return curr;
      const next = new Set(curr.allowed_broker_account_ids ?? []);
      if (enabled) {
        next.add(brokerAccountId);
      } else {
        next.delete(brokerAccountId);
      }
      return {
        ...curr,
        allowed_broker_account_ids: Array.from(next),
      };
    });
  };

  const allAccountsSelected =
    (telegramDraft?.allowed_broker_account_ids ?? []).length === 0;

  const allowDetectedChat = (
    chatId: string,
    userId: string,
    mode: "chat" | "user",
  ) => {
    if (mode === "chat") {
      const next = new Set(textToIdList(telegramChatIdsText));
      next.add(chatId);
      setTelegramChatIdsText(Array.from(next).join(", "));
    } else {
      const next = new Set(textToIdList(telegramUserIdsText));
      next.add(userId);
      setTelegramUserIdsText(Array.from(next).join(", "));
    }
  };

  const checklist = telegramDraft
    ? [
        {
          label: "Bot token saved",
          done: telegramStatus?.bot_token_present ?? false,
        },
        {
          label: "Agent service token ready",
          done: telegramStatus?.agent_service_token_present ?? false,
        },
        {
          label: "Bot API reachable",
          done: telegramStatus?.bot_api_reachable ?? false,
        },
        {
          label: "Chat or user allowlist set",
          done:
            textToIdList(telegramChatIdsText).length > 0 ||
            textToIdList(telegramUserIdsText).length > 0,
        },
        {
          label: "Inbound /start detected",
          done: (telegramStatus?.recent_chats ?? []).length > 0,
        },
        {
          label: "Agent enabled",
          done: telegramDraft.enabled,
        },
        {
          label: "Agent service active",
          done: telegramStatus?.service.active ?? false,
        },
        {
          label: "Delivery seen",
          done: Boolean(telegramStatus?.telemetry.last_notification_sent_at),
        },
      ]
    : [];

  return (
    <ConfigGrid>
      <ConfigPanel title="Telegram trade agent">
        {telegramDraft === null ? (
          <Unavailable
            title="Telegram agent config unavailable"
            detail={telegramError ?? "Loading Telegram agent settings."}
          />
        ) : (
          <>
            {telegramStatus && (
              <div className="tp-telegram-status">
                <div className="tp-telegram-metrics">
                  <Metric
                    label="Bot token"
                    value={yn(telegramStatus.bot_token_present)}
                  />
                  <Metric
                    label="Service token"
                    value={yn(telegramStatus.agent_service_token_present)}
                  />
                  <Metric
                    label="Bot API"
                    value={
                      telegramStatus.bot_api_reachable
                        ? "reachable"
                        : telegramStatus.bot_api_error
                          ? "error"
                          : "not checked"
                    }
                  />
                  <Metric label="Bot" value={telegramBotLabel(telegramStatus)} />
                  <Metric
                    label="Agent service"
                    value={
                      telegramStatus.service.active
                        ? "active"
                        : telegramStatus.service.status
                    }
                  />
                  <Metric
                    label="Last delivery"
                    value={formatOptionalDateTime(
                      telegramStatus.telemetry.last_notification_sent_at,
                    )}
                  />
                  <Metric
                    label="Last inbound"
                    value={formatOptionalDateTime(
                      telegramStatus.telemetry.last_update_received_at,
                    )}
                  />
                  <Metric
                    label="Event cursor"
                    value={
                      telegramStatus.telemetry.event_cursor === null
                        ? "n/a"
                        : String(telegramStatus.telemetry.event_cursor)
                    }
                  />
                </div>
                {telegramStatus.telemetry.last_error_message && (
                  <div className="tp-inline-error">
                    {telegramStatus.telemetry.last_error_type}:{" "}
                    {telegramStatus.telemetry.last_error_message}
                  </div>
                )}
                <div className="tp-telegram-checklist">
                  {checklist.map((item) => (
                    <span
                      key={item.label}
                      className={`tp-telegram-check ${item.done ? "ok" : "idle"}`}
                    >
                      {item.done ? "OK" : "--"} {item.label}
                    </span>
                  ))}
                </div>
                {telegramStatus.recent_chats.length > 0 && (
                  <div className="tp-telegram-detected">
                    {telegramStatus.recent_chats.map((chat) => (
                      <div
                        key={`${chat.chat_id}:${chat.user_id}`}
                        className="tp-telegram-detected-row"
                      >
                        <span>
                          {chat.username ? `@${chat.username}` : chat.first_name ?? "chat"} / chat{" "}
                          {chat.chat_id} / user {chat.user_id}
                        </span>
                        <button
                          type="button"
                          className="tp-btn tp-btn-secondary"
                          onClick={() =>
                            allowDetectedChat(chat.chat_id, chat.user_id, "chat")
                          }
                        >
                          Allow chat
                        </button>
                        <button
                          type="button"
                          className="tp-btn tp-btn-secondary"
                          onClick={() =>
                            allowDetectedChat(chat.chat_id, chat.user_id, "user")
                          }
                        >
                          Allow user
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="tp-crypto-form-grid">
              <label className="tp-toggle-row">
                <input
                  type="checkbox"
                  checked={telegramDraft.enabled}
                  onChange={(e) =>
                    setTelegramDraft((curr) =>
                      curr === null ? curr : { ...curr, enabled: e.target.checked },
                    )
                  }
                />
                Enable Telegram agent
              </label>
              <label className="tp-field">
                <span>Approval authority</span>
                <select
                  className="tp-select"
                  value={telegramDraft.authority_mode}
                  onChange={(e) =>
                    setTelegramDraft((curr) =>
                      curr === null
                        ? curr
                        : {
                            ...curr,
                            authority_mode:
                              e.target.value === "two_step_confirm"
                                ? "two_step_confirm"
                                : "notify_only",
                          },
                    )
                  }
                >
                  <option value="notify_only">Notify only</option>
                  <option value="two_step_confirm">Two-step confirm</option>
                </select>
              </label>
              <ConfigField
                label="Allowed chat IDs"
                value={telegramChatIdsText}
                onChange={setTelegramChatIdsText}
              />
              <ConfigField
                label="Allowed user IDs"
                value={telegramUserIdsText}
                onChange={setTelegramUserIdsText}
              />
              {accounts.length === 0 && (
                <ConfigField
                  label="Allowed broker account IDs"
                  value={telegramAccountIdsText}
                  onChange={setTelegramAccountIdsText}
                />
              )}
            </div>
            {accounts.length > 0 && (
              <div className="tp-crypto-actions">
                <label className="tp-toggle-row">
                  <input
                    type="checkbox"
                    checked={allAccountsSelected}
                    onChange={(e) =>
                      setTelegramDraft((curr) =>
                        curr === null
                          ? curr
                          : {
                              ...curr,
                              allowed_broker_account_ids: e.target.checked
                                ? []
                                : accounts.map(
                                    (account) => account.brokerAccountId,
                                  ),
                            },
                      )
                    }
                  />
                  All broker accounts
                </label>
                {accounts.map((account) => (
                  <label key={account.brokerAccountId} className="tp-toggle-row">
                    <input
                      type="checkbox"
                      disabled={allAccountsSelected}
                      checked={
                        allAccountsSelected ||
                        telegramDraft.allowed_broker_account_ids.includes(
                          account.brokerAccountId,
                        )
                      }
                      onChange={(e) =>
                        setAllowedAccountId(
                          account.brokerAccountId,
                          e.target.checked,
                        )
                      }
                    />
                    {account.label}
                  </label>
                ))}
              </div>
            )}
            <div className="tp-crypto-actions">
              {TELEGRAM_AGENT_CATEGORIES.map((category) => (
                <label key={category} className="tp-toggle-row">
                  <input
                    type="checkbox"
                    checked={telegramDraft.notification_categories.includes(category)}
                    onChange={(e) =>
                      setTelegramDraft((curr) => {
                        if (curr === null) return curr;
                        const next = new Set(curr.notification_categories);
                        if (e.target.checked) {
                          next.add(category);
                        } else {
                          next.delete(category);
                        }
                        return {
                          ...curr,
                          notification_categories: Array.from(next),
                        };
                      })
                    }
                  />
                  {category}
                </label>
              ))}
            </div>
            <div className="tp-crypto-actions">
              <button
                type="button"
                className="tp-btn tp-btn-primary"
                onClick={() => void saveTelegramConfig()}
                disabled={telegramSaving}
              >
                {telegramSaving ? "Saving..." : "Save Telegram agent"}
              </button>
              <button
                type="button"
                className="tp-btn tp-btn-secondary"
                onClick={() => void refreshTelegramConfig()}
                disabled={telegramSaving}
              >
                Refresh
              </button>
              <button
                type="button"
                className="tp-btn tp-btn-secondary"
                onClick={() => void testTelegramConnection()}
                disabled={telegramSaving || telegramConnectionTesting}
              >
                {telegramConnectionTesting ? "Testing..." : "Test bot connection"}
              </button>
              <button
                type="button"
                className="tp-btn tp-btn-secondary"
                onClick={() => void testTelegramNotification()}
                disabled={telegramSaving || telegramPipelineTesting}
              >
                {telegramPipelineTesting ? "Testing..." : "Test agent pipeline"}
              </button>
              <button
                type="button"
                className="tp-btn tp-btn-secondary"
                onClick={() => void restartTelegramAgent()}
                disabled={telegramSaving || telegramRestarting}
              >
                {telegramRestarting ? "Restarting..." : "Restart agent"}
              </button>
              {telegramMessage && (
                <span className="tp-inline-ok">{telegramMessage}</span>
              )}
              {telegramError && (
                <span className="tp-inline-error">{telegramError}</span>
              )}
            </div>
            {telegramConnectionResult && (
              <div className="tp-telegram-test-result">
                <span className={telegramConnectionResult.status === "ok" ? "ok" : "bad"}>
                  {telegramConnectionResult.status}
                </span>
                {telegramConnectionResult.recipients.map((recipient) => (
                  <span key={recipient.recipient_id}>
                    {recipient.recipient_id}:{" "}
                    {recipient.ok ? "sent" : recipient.detail}
                  </span>
                ))}
                {telegramConnectionResult.error && (
                  <span>{telegramConnectionResult.error}</span>
                )}
              </div>
            )}
            {telegramConfig?.updated_at && (
              <div className="cx-note">
                Last updated {new Date(telegramConfig.updated_at).toLocaleString()}
                {telegramConfig.updated_by ? ` by ${telegramConfig.updated_by}` : ""}
              </div>
            )}
          </>
        )}
      </ConfigPanel>
      <ConfigPanel title="Channel credentials">
        <div className="tp-crypto-form-grid">
          {CHANNEL_CREDENTIAL_FIELDS.map((field) => (
            <ConfigField
              key={field.key}
              label={field.label}
              type={field.secret ? "password" : "text"}
              value={credentialValues[field.key] ?? ""}
              onChange={(value) =>
                setCredentialValues((curr) => ({
                  ...curr,
                  [field.key]: value,
                }))
              }
            />
          ))}
        </div>
        <div className="tp-crypto-actions">
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={() => void saveChannelCredentials()}
            disabled={credentialsSaving}
          >
            {credentialsSaving ? "Saving..." : "Save channel keys"}
          </button>
          {credentialsMessage && (
            <span className="tp-inline-ok">{credentialsMessage}</span>
          )}
          {credentialsError && (
            <span className="tp-inline-error">{credentialsError}</span>
          )}
        </div>
      </ConfigPanel>
    </ConfigGrid>
  );
};

const AiTab = ({
  apiClient,
  systemHealth,
  pending,
  recentEvents,
  newsProviderName = "none",
  qualityTierWarning = false,
}: {
  apiClient: ApiClient;
  systemHealth: SystemHealthRow[];
  pending: Pending[];
  recentEvents: EventRow[];
  newsProviderName?: string;
  qualityTierWarning?: boolean;
}) => {
  const aiRows = systemHealth.filter((row) =>
    row.key === "ai" || row.label.toLowerCase().includes("ai"),
  );
  const aiReviewed = pending.filter((p) => p.ai > 0 || p.aiRiskFlags.length > 0);
  const [gateWindow, setGateWindow] = useState({
    startDate: daysAgoIso(29),
    endDate: todayIso(),
    operatorAttested: false,
  });
  const [gate, setGate] =
    useState<CryptoPaperValidationGateResponse | null>(null);
  const [gateError, setGateError] = useState<string | null>(null);
  const [gateLoading, setGateLoading] = useState<boolean>(false);
  const [dayForm, setDayForm] = useState<CryptoPaperValidationDayRequest>({
    validation_date: todayIso(),
    reviewed_by: "operator",
    manual_approval_required: true,
    crypto_signals: 0,
    approvals_created: 0,
    approvals_confirmed: 0,
    broker_orders_placed: 0,
    auto_approved_count: 0,
    guardrail_breaches: 0,
    realized_pnl: "0",
    notes: "",
  });
  const [daySaving, setDaySaving] = useState<boolean>(false);
  const [dayMessage, setDayMessage] = useState<string | null>(null);
  const [dayError, setDayError] = useState<string | null>(null);
  const [credentialValues, setCredentialValues] = useState<
    Partial<Record<keyof WizardCredentialsRequest, string>>
  >({});
  const [credentialsSaving, setCredentialsSaving] = useState<boolean>(false);
  const [credentialsMessage, setCredentialsMessage] = useState<string | null>(
    null,
  );
  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  const [cryptoConfig, setCryptoConfig] =
    useState<CryptoPaperTradingConfigWire | null>(null);
  const [cryptoConfigDraft, setCryptoConfigDraft] =
    useState<CryptoPaperTradingConfigWire | null>(null);
  const [cryptoConfigSaving, setCryptoConfigSaving] = useState<boolean>(false);
  const [cryptoConfigMessage, setCryptoConfigMessage] = useState<string | null>(
    null,
  );
  const [cryptoConfigError, setCryptoConfigError] = useState<string | null>(null);
  const [aiProviderDrafts, setAiProviderDrafts] = useState<AIProviderConfigWire[]>(
    [],
  );
  const [aiProviderLoading, setAiProviderLoading] = useState<boolean>(false);
  const [aiProviderSaving, setAiProviderSaving] = useState<string | null>(null);
  const [aiProviderMessage, setAiProviderMessage] = useState<string | null>(null);
  const [aiProviderError, setAiProviderError] = useState<string | null>(null);
  const [aiPreflight, setAiPreflight] =
    useState<AIProviderPreflightResponseWire | null>(null);
  const [aiBreakerResetting, setAiBreakerResetting] = useState<boolean>(false);
  const [positionReviewConfig, setPositionReviewConfig] =
    useState<PositionReassessmentConfigWire | null>(null);
  const [positionReviewDraft, setPositionReviewDraft] =
    useState<PositionReassessmentConfigWire | null>(null);
  const [positionReviewSaving, setPositionReviewSaving] =
    useState<boolean>(false);
  const [positionReviewMessage, setPositionReviewMessage] =
    useState<string | null>(null);
  const [positionReviewError, setPositionReviewError] =
    useState<string | null>(null);

  const refreshCryptoConfig = async () => {
    setCryptoConfigError(null);
    try {
      const result = await apiClient.getCryptoPaperConfig();
      setCryptoConfig(result);
      setCryptoConfigDraft(result);
    } catch (e) {
      setCryptoConfigError(problemMessage(e, "Failed to load crypto paper config"));
    }
  };

  useEffect(() => {
    void refreshCryptoConfig();
  }, [apiClient]);

  const refreshAIProviders = async () => {
    setAiProviderLoading(true);
    setAiProviderError(null);
    try {
      const [result, preflight] = await Promise.all([
        apiClient.listAIProviderConfigs(),
        apiClient.getAIProviderPreflight(),
      ]);
      setAiProviderDrafts(result.configs);
      setAiPreflight(preflight);
    } catch (e) {
      setAiProviderError(problemMessage(e, "Failed to load AI provider config"));
    } finally {
      setAiProviderLoading(false);
    }
  };

  useEffect(() => {
    void refreshAIProviders();
  }, [apiClient]);

  const refreshPositionReviewConfig = async () => {
    setPositionReviewError(null);
    try {
      const result = await apiClient.getPositionReassessmentConfig();
      setPositionReviewConfig(result);
      setPositionReviewDraft(result);
    } catch (e) {
      setPositionReviewError(
        problemMessage(e, "Failed to load position review config"),
      );
    }
  };

  useEffect(() => {
    void refreshPositionReviewConfig();
  }, [apiClient]);

  const updateAIProviderDraft = (
    feature: string,
    patch: Partial<AIProviderConfigWire>,
  ) => {
    setAiProviderDrafts((curr) =>
      curr.map((config) =>
        config.feature === feature ? { ...config, ...patch } : config,
      ),
    );
  };

  const saveAIProvider = async (config: AIProviderConfigWire) => {
    setAiProviderSaving(config.feature);
    setAiProviderMessage(null);
    setAiProviderError(null);
    try {
      const saved = await apiClient.putAIProviderConfig(config.feature, config);
      setAiProviderDrafts((curr) =>
        curr.map((item) => (item.feature === saved.feature ? saved : item)),
      );
      setAiProviderMessage(`${AI_FEATURE_LABELS[saved.feature] ?? saved.feature} saved`);
    } catch (e) {
      setAiProviderError(problemMessage(e, "Failed to save AI provider config"));
    } finally {
      setAiProviderSaving(null);
    }
  };

  const savePositionReviewConfig = async () => {
    if (positionReviewDraft === null) return;
    setPositionReviewSaving(true);
    setPositionReviewMessage(null);
    setPositionReviewError(null);
    try {
      const result = await apiClient.setPositionReassessmentConfig({
        enabled: positionReviewDraft.enabled,
        cadence_minutes: positionReviewDraft.cadence_minutes,
        notification_min_severity: (
          positionReviewDraft.notification_min_severity
        ),
      });
      setPositionReviewConfig(result);
      setPositionReviewDraft(result);
      setPositionReviewMessage("Position review settings saved");
    } catch (e) {
      setPositionReviewError(
        problemMessage(e, "Failed to save position review config"),
      );
    } finally {
      setPositionReviewSaving(false);
    }
  };

  const resetAIScoringBreaker = async () => {
    if (
      !window.confirm(
        "Reset the AI scoring breaker after confirming provider recovery?",
      )
    ) {
      return;
    }
    setAiBreakerResetting(true);
    setAiProviderMessage(null);
    setAiProviderError(null);
    try {
      const result = await apiClient.resetAIBreaker(
        "signal_scoring",
        "Operator confirmed provider recovery from Config AI panel",
      );
      setAiProviderMessage(`${result.feature} breaker reset`);
      await refreshAIProviders();
    } catch (e) {
      setAiProviderError(problemMessage(e, "Failed to reset AI breaker"));
    } finally {
      setAiBreakerResetting(false);
    }
  };

  const refreshGate = async () => {
    setGateLoading(true);
    setGateError(null);
    try {
      const result = await apiClient.getCryptoPaperValidationStatus({
        startDate: gateWindow.startDate,
        endDate: gateWindow.endDate,
        operatorAttestedMostlyProfitable: gateWindow.operatorAttested,
      });
      setGate(result);
    } catch (e) {
      setGateError(problemMessage(e, "Failed to load crypto validation"));
    } finally {
      setGateLoading(false);
    }
  };

  const saveDay = async () => {
    setDaySaving(true);
    setDayMessage(null);
    setDayError(null);
    try {
      await apiClient.postCryptoPaperValidationDay(dayForm);
      setDayMessage(`Recorded ${dayForm.validation_date}`);
      await refreshGate();
    } catch (e) {
      setDayError(problemMessage(e, "Failed to record validation day"));
    } finally {
      setDaySaving(false);
    }
  };

  const saveCredentials = async () => {
    const payload: WizardCredentialsRequest = {};
    for (const field of [
      ...AI_CREDENTIAL_FIELDS,
      ...CRYPTO_CREDENTIAL_FIELDS,
      ...RESEARCH_CREDENTIAL_FIELDS,
    ]) {
      const value = credentialValues[field.key]?.trim();
      if (value) {
        payload[field.key] = value;
      }
    }
    if (Object.keys(payload).length === 0) {
      setCredentialsError("Enter at least one value.");
      setCredentialsMessage(null);
      return;
    }
    setCredentialsSaving(true);
    setCredentialsError(null);
    setCredentialsMessage(null);
    try {
      const result = await apiClient.postWizardCredentials(payload);
      setCredentialsMessage(
        result.updated.length > 0
          ? `Updated ${result.updated.join(", ")}`
          : "No values changed",
      );
      setCredentialValues({});
    } catch (e) {
      setCredentialsError(problemMessage(e, "Failed to save credentials"));
    } finally {
      setCredentialsSaving(false);
    }
  };

  const saveCryptoConfig = async () => {
    if (cryptoConfigDraft === null) return;
    setCryptoConfigSaving(true);
    setCryptoConfigMessage(null);
    setCryptoConfigError(null);
    try {
      const result = await apiClient.putCryptoPaperConfig({
        auto_trading_enabled: cryptoConfigDraft.auto_trading_enabled,
        auto_approval_enabled: cryptoConfigDraft.auto_approval_enabled,
        max_open_trades: cryptoConfigDraft.max_open_trades,
        max_daily_entries: cryptoConfigDraft.max_daily_entries,
        risk_per_trade_pct: cryptoConfigDraft.risk_per_trade_pct,
      });
      setCryptoConfig(result);
      setCryptoConfigDraft(result);
      setCryptoConfigMessage("Crypto paper settings saved");
    } catch (e) {
      setCryptoConfigError(problemMessage(e, "Failed to save crypto paper config"));
    } finally {
      setCryptoConfigSaving(false);
    }
  };

  return (
    <>
      {qualityTierWarning && (
        <div
          role="alert"
          style={{
            background: "var(--tp-warn, #7a5800)",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: 6,
            marginBottom: 14,
            fontWeight: 500,
          }}
        >
          News provider quality warning — current provider is{" "}
          <strong>{newsProviderName}</strong>. Switch to Benzinga for
          full-quality AI inputs (PRD §4.14 quality tier).
        </div>
      )}
    <ConfigGrid>
      <ConfigPanel title="AI service">
        <HealthRows rows={aiRows} />
        <Metric label="Pending approvals with AI data" value={String(aiReviewed.length)} />
        <Metric
          label="Average AI confidence"
          value={
            aiReviewed.length === 0
              ? "n/a"
              : `${Math.round(
                  aiReviewed.reduce((sum, p) => sum + p.ai, 0) /
                    aiReviewed.length,
                )}%`
          }
        />
      </ConfigPanel>
      <ConfigPanel title="AI audit events">
        <EventList events={filterEvents(recentEvents, ["ai", "model", "prompt"])} />
        <Unavailable
          title="AI mode edit endpoint unavailable"
          detail="The wizard can persist initial AI mode, but the app does not expose a dedicated runtime AI-mode configuration endpoint yet."
        />
      </ConfigPanel>
      <ConfigPanel title="Position review controls">
        {positionReviewDraft === null ? (
          <Unavailable
            title="Position review config unavailable"
            detail={positionReviewError ?? "Loading position review settings."}
          />
        ) : (
          <>
            <div className="tp-crypto-form-grid">
              <label className="tp-toggle-row">
                <input
                  type="checkbox"
                  checked={positionReviewDraft.enabled}
                  onChange={(e) =>
                    setPositionReviewDraft((curr) =>
                      curr === null
                        ? curr
                        : { ...curr, enabled: e.target.checked },
                    )
                  }
                />
                Scheduled position reviews
              </label>
              <ConfigNumberField
                label="Cadence minutes"
                value={positionReviewDraft.cadence_minutes}
                onChange={(value) =>
                  setPositionReviewDraft((curr) =>
                    curr === null
                      ? curr
                      : { ...curr, cadence_minutes: value },
                  )
                }
              />
              <label className="tp-field">
                <span>Notify from severity</span>
                <select
                  className="tp-select"
                  value={positionReviewDraft.notification_min_severity}
                  onChange={(e) =>
                    setPositionReviewDraft((curr) =>
                      curr === null
                        ? curr
                        : {
                            ...curr,
                            notification_min_severity: e.target.value as
                              | "low"
                              | "medium"
                              | "high",
                          },
                    )
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </label>
            </div>
            <div className="tp-crypto-actions">
              <button
                type="button"
                className="tp-btn tp-btn-primary"
                onClick={() => void savePositionReviewConfig()}
                disabled={positionReviewSaving}
              >
                {positionReviewSaving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                className="tp-btn tp-btn-secondary"
                onClick={() => void refreshPositionReviewConfig()}
                disabled={positionReviewSaving}
              >
                Refresh
              </button>
              {positionReviewMessage && (
                <span className="tp-inline-ok">{positionReviewMessage}</span>
              )}
              {positionReviewError && (
                <span className="tp-inline-error">{positionReviewError}</span>
              )}
            </div>
            {positionReviewConfig?.updated_at && (
              <div className="cx-note">
                Last updated{" "}
                {new Date(positionReviewConfig.updated_at).toLocaleString()}
                {positionReviewConfig.updated_by
                  ? ` by ${positionReviewConfig.updated_by}`
                  : ""}
              </div>
            )}
          </>
        )}
      </ConfigPanel>
      <ConfigPanel title="AI model routing">
        {aiPreflight !== null && (
          <div className="cx-note">
            Provider preflight:{" "}
            {Object.entries(aiPreflight.features).map(([feature, value]) => {
              const status =
                value !== null &&
                typeof value === "object" &&
                "available" in value
                  ? (value as { available?: unknown }).available
                  : null;
              return `${AI_FEATURE_LABELS[feature] ?? feature}=${
                status === true ? "available" : "check"
              }`;
            }).join(" | ")}
          </div>
        )}
        {aiProviderLoading && (
          <div className="cx-note">Loading provider routes.</div>
        )}
        {aiProviderDrafts.length === 0 && !aiProviderLoading ? (
          <Unavailable
            title="AI provider config unavailable"
            detail={aiProviderError ?? "No provider routes returned."}
          />
        ) : (
          <div className="tp-crypto-form-grid">
            {aiProviderDrafts.map((config) => (
              <Fragment key={config.feature}>
                <label className="tp-toggle-row">
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={(e) =>
                      updateAIProviderDraft(config.feature, {
                        enabled: e.target.checked,
                      })
                    }
                  />
                  {AI_FEATURE_LABELS[config.feature] ?? config.feature}
                </label>
                <label className="tp-field">
                  <span>Provider</span>
                  <select
                    className="tp-select"
                    value={config.provider}
                    onChange={(e) =>
                      updateAIProviderDraft(config.feature, {
                        provider: e.target.value,
                        base_url: null,
                      })
                    }
                  >
                    {AI_PROVIDER_OPTIONS.map((provider) => (
                      <option key={provider} value={provider}>
                        {provider}
                      </option>
                    ))}
                  </select>
                </label>
                <ConfigField
                  label="Model"
                  value={config.model_id}
                  onChange={(value) =>
                    updateAIProviderDraft(config.feature, { model_id: value })
                  }
                />
                {config.provider === "ollama" && (
                  <div className="cx-note">
                    Ollama/local models may have weaker structured-output,
                    tool-choice, and latency behavior. Shadow-test before use
                    on approval-adjacent surfaces.
                  </div>
                )}
                <ConfigNumberField
                  label="Timeout"
                  value={config.timeout_seconds}
                  onChange={(value) =>
                    updateAIProviderDraft(config.feature, {
                      timeout_seconds: value,
                    })
                  }
                />
                <button
                  type="button"
                  className="tp-btn tp-btn-secondary"
                  disabled={aiProviderSaving === config.feature}
                  onClick={() => void saveAIProvider(config)}
                >
                  {aiProviderSaving === config.feature ? "Saving..." : "Save"}
                </button>
              </Fragment>
            ))}
          </div>
        )}
        <div className="tp-crypto-actions">
          <button
            type="button"
            className="tp-btn tp-btn-secondary"
            onClick={() => void refreshAIProviders()}
            disabled={aiProviderLoading}
          >
            Refresh
          </button>
          <button
            type="button"
            className="tp-btn tp-btn-secondary"
            onClick={() => void resetAIScoringBreaker()}
            disabled={aiBreakerResetting}
          >
            {aiBreakerResetting ? "Resetting..." : "Reset scoring breaker"}
          </button>
          {aiProviderMessage && (
            <span className="tp-inline-ok">{aiProviderMessage}</span>
          )}
          {aiProviderError && (
            <span className="tp-inline-error">{aiProviderError}</span>
          )}
        </div>
      </ConfigPanel>
      <ConfigPanel title="AI provider keys">
        <div className="tp-crypto-form-grid">
          {AI_CREDENTIAL_FIELDS.map((field) => (
            <ConfigField
              key={field.key}
              label={field.label}
              type={field.secret ? "password" : "text"}
              value={credentialValues[field.key] ?? ""}
              onChange={(value) =>
                setCredentialValues((curr) => ({
                  ...curr,
                  [field.key]: value,
                }))
              }
            />
          ))}
        </div>
        <div className="tp-crypto-actions">
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={() => void saveCredentials()}
            disabled={credentialsSaving}
          >
            Save AI keys
          </button>
          {credentialsMessage && (
            <span className="tp-inline-ok">{credentialsMessage}</span>
          )}
          {credentialsError && (
            <span className="tp-inline-error">{credentialsError}</span>
          )}
        </div>
      </ConfigPanel>
      <ConfigPanel title="Crypto providers">
        <div className="tp-crypto-form-grid">
          {CRYPTO_CREDENTIAL_FIELDS.map((field) => (
            <ConfigField
              key={field.key}
              label={field.label}
              type={field.secret ? "password" : "text"}
              value={credentialValues[field.key] ?? ""}
              onChange={(value) =>
                setCredentialValues((curr) => ({
                  ...curr,
                  [field.key]: value,
                }))
              }
            />
          ))}
        </div>
        <div className="tp-crypto-actions">
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={() => void saveCredentials()}
            disabled={credentialsSaving}
          >
            Save provider keys
          </button>
          {credentialsMessage && (
            <span className="tp-inline-ok">{credentialsMessage}</span>
          )}
          {credentialsError && (
            <span className="tp-inline-error">{credentialsError}</span>
          )}
        </div>
      </ConfigPanel>
      <ConfigPanel title="Research feeds (Moonshots)">
        <p className="tp-config-hint">
          Optional enrichment for the Moonshots research queue. Without
          these, rows show data quality &ldquo;limited&rdquo; and rank lower.
          Display-only — these never place trades.
        </p>
        <div className="tp-crypto-form-grid">
          {RESEARCH_CREDENTIAL_FIELDS.map((field) => (
            <ConfigField
              key={field.key}
              label={field.label}
              type={field.secret ? "password" : "text"}
              value={credentialValues[field.key] ?? ""}
              onChange={(value) =>
                setCredentialValues((curr) => ({
                  ...curr,
                  [field.key]: value,
                }))
              }
            />
          ))}
        </div>
        <div className="tp-crypto-actions">
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={() => void saveCredentials()}
            disabled={credentialsSaving}
          >
            Save research feed keys
          </button>
          {credentialsMessage && (
            <span className="tp-inline-ok">{credentialsMessage}</span>
          )}
          {credentialsError && (
            <span className="tp-inline-error">{credentialsError}</span>
          )}
        </div>
      </ConfigPanel>
      <ConfigPanel title="Crypto paper controls">
        {cryptoConfigDraft === null ? (
          <Unavailable
            title="Crypto paper config unavailable"
            detail={cryptoConfigError ?? "Loading crypto paper settings."}
          />
        ) : (
          <>
            <div className="tp-crypto-form-grid">
              <label className="tp-toggle-row">
                <input
                  type="checkbox"
                  checked={cryptoConfigDraft.auto_trading_enabled}
                  onChange={(e) =>
                    setCryptoConfigDraft((curr) =>
                      curr === null
                        ? curr
                        : { ...curr, auto_trading_enabled: e.target.checked },
                    )
                  }
                />
                Auto paper trading
              </label>
              <label className="tp-toggle-row">
                <input
                  type="checkbox"
                  checked={cryptoConfigDraft.auto_approval_enabled}
                  onChange={(e) =>
                    setCryptoConfigDraft((curr) =>
                      curr === null
                        ? curr
                        : { ...curr, auto_approval_enabled: e.target.checked },
                    )
                  }
                />
                Auto approve crypto signals
              </label>
              <ConfigNumberField
                label="Max open trades"
                value={cryptoConfigDraft.max_open_trades}
                onChange={(value) =>
                  setCryptoConfigDraft((curr) =>
                    curr === null ? curr : { ...curr, max_open_trades: value },
                  )
                }
              />
              <ConfigNumberField
                label="Max daily entries"
                value={cryptoConfigDraft.max_daily_entries}
                onChange={(value) =>
                  setCryptoConfigDraft((curr) =>
                    curr === null ? curr : { ...curr, max_daily_entries: value },
                  )
                }
              />
              <ConfigField
                label="Risk per trade %"
                value={cryptoConfigDraft.risk_per_trade_pct}
                onChange={(value) =>
                  setCryptoConfigDraft((curr) =>
                    curr === null ? curr : { ...curr, risk_per_trade_pct: value },
                  )
                }
              />
            </div>
            <div className="tp-crypto-actions">
              <button
                type="button"
                className="tp-btn tp-btn-primary"
                onClick={() => void saveCryptoConfig()}
                disabled={cryptoConfigSaving}
              >
                Save crypto paper controls
              </button>
              <button
                type="button"
                className="tp-btn tp-btn-secondary"
                onClick={() => void refreshCryptoConfig()}
                disabled={cryptoConfigSaving}
              >
                Refresh
              </button>
              {cryptoConfigMessage && (
                <span className="tp-inline-ok">{cryptoConfigMessage}</span>
              )}
              {cryptoConfigError && (
                <span className="tp-inline-error">{cryptoConfigError}</span>
              )}
            </div>
            {cryptoConfig?.updated_at && (
              <div className="cx-note">
                Last updated {new Date(cryptoConfig.updated_at).toLocaleString()}
                {cryptoConfig.updated_by ? ` by ${cryptoConfig.updated_by}` : ""}
              </div>
            )}
          </>
        )}
      </ConfigPanel>
      <ConfigPanel title="Crypto paper-validation gate">
        <div className="tp-crypto-controls">
          <ConfigField
            label="Start"
            type="date"
            value={gateWindow.startDate}
            onChange={(value) =>
              setGateWindow((curr) => ({ ...curr, startDate: value }))
            }
          />
          <ConfigField
            label="End"
            type="date"
            value={gateWindow.endDate}
            onChange={(value) =>
              setGateWindow((curr) => ({ ...curr, endDate: value }))
            }
          />
          <label className="tp-toggle-row">
            <input
              type="checkbox"
              checked={gateWindow.operatorAttested}
              onChange={(e) =>
                setGateWindow((curr) => ({
                  ...curr,
                  operatorAttested: e.target.checked,
                }))
              }
            />
            Mostly profitable
          </label>
          <button
            type="button"
            className="tp-btn tp-btn-secondary"
            onClick={() => void refreshGate()}
            disabled={gateLoading}
          >
            Refresh gate
          </button>
        </div>
        {gateError && <div className="tp-inline-error">{gateError}</div>}
        {gate && (
          <>
            <div className="cx-regime-grid" style={{ marginTop: 12 }}>
              <div className="cell">
                <span className="k">Days</span>
                <span className="v">
                  {gate.days_reviewed}/{gate.days_required}
                </span>
              </div>
              <div className="cell">
                <span className="k">Realized P&L</span>
                <span className="v small">${gate.total_realized_pnl}</span>
              </div>
              <div className="cell">
                <span className="k">Gate</span>
                <span className="v small">{gate.passed ? "Pass" : "Blocked"}</span>
              </div>
            </div>
            {gate.failures.length > 0 && (
              <ul className="tp-crypto-failures">
                {gate.failures.map((failure) => (
                  <li key={failure}>{failure}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </ConfigPanel>
      <ConfigPanel title="Record crypto validation day">
        <div className="tp-crypto-form-grid">
          <ConfigField
            label="Date"
            type="date"
            value={dayForm.validation_date}
            onChange={(value) =>
              setDayForm((curr) => ({ ...curr, validation_date: value }))
            }
          />
          <ConfigField
            label="Reviewed by"
            value={dayForm.reviewed_by}
            onChange={(value) =>
              setDayForm((curr) => ({ ...curr, reviewed_by: value }))
            }
          />
          <ConfigNumberField
            label="Signals"
            value={dayForm.crypto_signals}
            onChange={(value) =>
              setDayForm((curr) => ({ ...curr, crypto_signals: value }))
            }
          />
          <ConfigNumberField
            label="Approvals created"
            value={dayForm.approvals_created}
            onChange={(value) =>
              setDayForm((curr) => ({ ...curr, approvals_created: value }))
            }
          />
          <ConfigNumberField
            label="Approvals confirmed"
            value={dayForm.approvals_confirmed}
            onChange={(value) =>
              setDayForm((curr) => ({ ...curr, approvals_confirmed: value }))
            }
          />
          <ConfigNumberField
            label="Broker orders"
            value={dayForm.broker_orders_placed}
            onChange={(value) =>
              setDayForm((curr) => ({ ...curr, broker_orders_placed: value }))
            }
          />
          <ConfigNumberField
            label="Auto-approved"
            value={dayForm.auto_approved_count}
            onChange={(value) =>
              setDayForm((curr) => ({ ...curr, auto_approved_count: value }))
            }
          />
          <ConfigNumberField
            label="Guardrail breaches"
            value={dayForm.guardrail_breaches}
            onChange={(value) =>
              setDayForm((curr) => ({ ...curr, guardrail_breaches: value }))
            }
          />
          <ConfigField
            label="Realized P&L"
            value={dayForm.realized_pnl}
            onChange={(value) =>
              setDayForm((curr) => ({ ...curr, realized_pnl: value }))
            }
          />
          <label className="tp-toggle-row">
            <input
              type="checkbox"
              checked={dayForm.manual_approval_required}
              onChange={(e) =>
                setDayForm((curr) => ({
                  ...curr,
                  manual_approval_required: e.target.checked,
                }))
              }
            />
            Manual approval required
          </label>
        </div>
        <label className="tp-crypto-notes">
          Notes
          <textarea
            className="tp-input"
            rows={3}
            value={dayForm.notes}
            onChange={(e) =>
              setDayForm((curr) => ({ ...curr, notes: e.target.value }))
            }
          />
        </label>
        <div className="tp-crypto-actions">
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={() => void saveDay()}
            disabled={daySaving}
          >
            Save day
          </button>
          {dayMessage && <span className="tp-inline-ok">{dayMessage}</span>}
          {dayError && <span className="tp-inline-error">{dayError}</span>}
        </div>
      </ConfigPanel>
    </ConfigGrid>
    </>
  );
};

const StrategiesTab = ({
  pending,
  positions,
  recentEvents,
  listStrategyRoutes,
  updateStrategyRoute,
}: {
  pending: Pending[];
  positions: Position[];
  recentEvents: EventRow[];
  listStrategyRoutes: () => Promise<StrategyRoute[]>;
  updateStrategyRoute: (args: {
    strategyId: string;
    brokerAccountId: string;
    operatorNote?: string | null;
  }) => Promise<StrategyRoute>;
}) => {
  const [routes, setRoutes] = useState<StrategyRoute[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshRoutes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await listStrategyRoutes();
      setRoutes(next);
      setDrafts(
        Object.fromEntries(
          next.map((route) => [route.strategyId, route.brokerAccountId]),
        ),
      );
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not load strategy routes.",
      );
    } finally {
      setLoading(false);
    }
  }, [listStrategyRoutes]);

  useEffect(() => {
    void refreshRoutes();
  }, [refreshRoutes]);

  const saveRoute = async (route: StrategyRoute) => {
    const brokerAccountId = drafts[route.strategyId] ?? route.brokerAccountId;
    if (brokerAccountId === route.brokerAccountId) return;
    setSaving(route.strategyId);
    setMessage(null);
    setError(null);
    try {
      const updated = await updateStrategyRoute({
        strategyId: route.strategyId,
        brokerAccountId,
        operatorNote: "Changed from Config Strategies route table",
      });
      setRoutes((rows) =>
        rows.map((row) =>
          row.strategyId === updated.strategyId ? updated : row,
        ),
      );
      setDrafts((curr) => ({
        ...curr,
        [updated.strategyId]: updated.brokerAccountId,
      }));
      setMessage(`${updated.name} now routes to ${updated.accountLabel}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save route.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <ConfigGrid>
      <ConfigPanel title="Strategy routes">
        {loading ? (
          <Unavailable title="Loading routes" detail="Reading strategy account bindings." />
        ) : routes.length === 0 ? (
          <Unavailable
            title="No strategy routes"
            detail="No strategy registrations are available."
          />
        ) : (
          <div className="tp-strategy-route-list">
            {routes.map((route) => {
              const draft = drafts[route.strategyId] ?? route.brokerAccountId;
              const selected = route.routeTargets.find(
                (target) => target.brokerAccountId === draft,
              );
              const changed = draft !== route.brokerAccountId;
              const liveUnavailable = route.routeTargets.some(
                (target) => target.accountType === "trading_live",
              )
                ? null
                : `Trading Live unavailable: no enabled live ${route.assetClass} account provisioned.`;
              return (
                <div className="tp-strategy-route-row" key={route.strategyId}>
                  <div className="tp-strategy-route-main">
                    <div>
                      <div className="name">{route.name}</div>
                      <div className="meta">
                        {route.strategyId} · {route.timeframe} · {route.status}
                      </div>
                    </div>
                    <div className={"tp-route-pill " + route.accountType}>
                      {route.accountType === "trading_live"
                        ? "Trading Live"
                        : "Trading Paper"}
                    </div>
                  </div>
                  <div className="tp-route-controls">
                    <select
                      className="tp-input"
                      aria-label={`Route account for ${route.name}`}
                      value={draft}
                      onChange={(event) =>
                        setDrafts((curr) => ({
                          ...curr,
                          [route.strategyId]: event.target.value,
                        }))
                      }
                      disabled={saving === route.strategyId}
                    >
                      {route.routeTargets.map((target) => (
                        <option
                          key={target.brokerAccountId}
                          value={target.brokerAccountId}
                          disabled={!target.enabled}
                        >
                          {target.accountType === "trading_live"
                            ? "Trading Live"
                            : "Trading Paper"}{" "}
                          · {target.label}
                          {!target.enabled && target.blockedReason
                            ? ` · ${routeBlockedLabel(target.blockedReason)}`
                            : ""}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="tp-btn tp-btn-primary tp-btn-sm"
                      onClick={() => void saveRoute(route)}
                      disabled={
                        !changed ||
                        saving === route.strategyId ||
                        selected?.enabled === false
                      }
                    >
                      {saving === route.strategyId ? "Saving..." : "Save"}
                    </button>
                  </div>
                  {(selected?.blockedReason || liveUnavailable) && (
                    <div className="tp-route-note">
                      {selected?.blockedReason
                        ? routeBlockedLabel(selected.blockedReason)
                        : liveUnavailable}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="tp-route-footer">
          <button
            type="button"
            className="tp-btn tp-btn-secondary tp-btn-sm"
            onClick={() => void refreshRoutes()}
            disabled={loading}
          >
            Refresh
          </button>
          {message && <span className="tp-inline-ok">{message}</span>}
          {error && <span className="tp-inline-error">{error}</span>}
        </div>
      </ConfigPanel>
      <ConfigPanel title="Strategy events">
        <div className="tp-config-record">
          <div>
            <div className="name">Pending approvals</div>
            <div className="meta">
              {pending.length} pending · {positions.length} open positions
            </div>
          </div>
        </div>
        <EventList
          events={filterEvents(recentEvents, [
            "signal",
            "strategy",
            "approval",
            "regime",
          ])}
        />
      </ConfigPanel>
    </ConfigGrid>
  );
};

function routeBlockedLabel(reason: string): string {
  const labels: Record<string, string> = {
    account_disabled: "Account disabled",
    asset_class_mismatch: "Asset class mismatch",
    live_authorization_gates_incomplete: "Live gates incomplete",
    live_routing_equity_only: "Live routing is equity-only",
    unsupported_account_type: "Unsupported account type",
  };
  return labels[reason] ?? reason;
}

const UniverseTab = ({
  pending,
  positions,
  recentEvents,
}: {
  pending: Pending[];
  positions: Position[];
  recentEvents: EventRow[];
}) => {
  const symbols = Array.from(
    new Set([...pending.map((p) => p.sym), ...positions.map((p) => p.sym)]),
  ).sort();
  return (
    <ConfigGrid>
      <ConfigPanel title="Symbols in current workflow">
        {symbols.length === 0 ? (
          <Unavailable
            title="No active symbols"
            detail="No pending approvals or open positions are currently loaded."
          />
        ) : (
          <div className="tp-token-row">
            {symbols.map((sym) => (
              <span className="tp-token" key={sym}>{sym}</span>
            ))}
          </div>
        )}
      </ConfigPanel>
      <ConfigPanel title="Universe data events">
        <EventList
          events={filterEvents(recentEvents, [
            "universe",
            "membership",
            "index",
            "signal",
          ])}
        />
        <Unavailable
          title="Universe edit endpoint unavailable"
          detail="Point-in-time membership storage exists server-side, but the UI has no endpoint yet for reading or editing universe mode, symbol include/exclude lists, or PIT provider status."
        />
      </ConfigPanel>
    </ConfigGrid>
  );
};

const LensesTab = ({
  pending,
  recentEvents,
}: {
  pending: Pending[];
  recentEvents: EventRow[];
}) => {
  const lensRows = pending.filter(
    (p) => p.lensFlag || p.aiRiskFlags.length > 0,
  );
  return (
    <ConfigGrid>
      <ConfigPanel title="Lens flags on pending approvals">
        {lensRows.length === 0 ? (
          <Unavailable
            title="No active lens flags"
            detail="Pending approvals currently carry no lens flags or AI risk flags."
          />
        ) : (
          lensRows.map((p) => (
            <div className="tp-config-record" key={p.approvalId}>
              <div>
                <div className="name">{p.sym}</div>
                <div className="meta">{p.strategy}</div>
              </div>
              <div className="meta">
                {[p.lensFlag, ...p.aiRiskFlags].filter(Boolean).join(", ")}
              </div>
            </div>
          ))
        )}
      </ConfigPanel>
      <ConfigPanel title="Lens events">
        <EventList events={filterEvents(recentEvents, ["lens", "ai", "risk_flag"])} />
        <Unavailable
          title="Lens configuration endpoint unavailable"
          detail="The approval review surface can display emitted lens metadata, but there is no UI-safe endpoint yet to configure lens rules."
        />
      </ConfigPanel>
    </ConfigGrid>
  );
};

const BasketsTab = ({
  pending,
  recentEvents,
}: {
  pending: Pending[];
  recentEvents: EventRow[];
}) => {
  const baskets = pending.flatMap((p) =>
    p.basket ? [{ ...p.basket, approvalId: p.approvalId, sym: p.sym }] : [],
  );
  return (
    <ConfigGrid>
      <ConfigPanel title="Basket exposure on pending approvals">
        {baskets.length === 0 ? (
          <Unavailable
            title="No basket-backed pending approvals"
            detail="Pending approvals currently carry no basket exposure metadata."
          />
        ) : (
          baskets.map((b) => (
            <div className="tp-config-record" key={b.approvalId}>
              <div>
                <div className="name">{b.name}</div>
                <div className="meta">{b.sym}</div>
              </div>
              <Metric label="Before" value={`${b.exposureBefore}%`} />
              <Metric label="After" value={`${b.exposureAfter}%`} />
              <Metric label="Cap" value={`${b.cap}%`} />
            </div>
          ))
        )}
      </ConfigPanel>
      <ConfigPanel title="Basket events">
        <EventList events={filterEvents(recentEvents, ["basket"])} />
        <Unavailable
          title="Basket configuration endpoint unavailable"
          detail="No backend read/write contract exists yet for basket definitions, member lists, or exposure caps."
        />
      </ConfigPanel>
    </ConfigGrid>
  );
};

const SystemTab = ({
  systemHealth,
  recentEvents,
}: {
  systemHealth: SystemHealthRow[];
  recentEvents: EventRow[];
}) => (
  <ConfigGrid>
    <ConfigPanel title="Subsystem status">
      <HealthRows rows={systemHealth} />
    </ConfigPanel>
    <ConfigPanel title="Recent system events">
      <EventList
        events={filterEvents(recentEvents, [
          "system",
          "backup",
          "restore",
          "halt",
          "scheduler",
          "health",
        ])}
      />
    </ConfigPanel>
  </ConfigGrid>
);

const ConfigField = ({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) => (
  <label className="tp-crypto-field">
    {label}
    <input
      className="tp-input"
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

const ConfigNumberField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) => (
  <ConfigField
    label={label}
    type="number"
    value={String(value)}
    onChange={(raw) => onChange(Math.max(0, Number(raw) || 0))}
  />
);

const ConfigGrid = ({ children }: { children: ReactNode }) => (
  <div className="tp-config-grid">{children}</div>
);

const ConfigPanel = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className="tp-tile">
    <div className="tp-tile-head">
      <span className="label">{title}</span>
    </div>
    <div className="tp-tile-body">{children}</div>
  </div>
);

const Metric = ({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}) => (
  <div className="tp-config-metric">
    <span className="k">{label}</span>
    <span className="v">{value ?? "n/a"}</span>
  </div>
);

const Unavailable = ({
  title,
  detail,
}: {
  title: string;
  detail: string;
}) => (
  <div className="tp-config-unavailable">
    <div className="title">{title}</div>
    <div className="detail">{detail}</div>
  </div>
);

const HealthRows = ({ rows }: { rows: SystemHealthRow[] }) => {
  if (rows.length === 0) {
    return (
      <Unavailable
        title="No health rows"
        detail="The health endpoint is wired, but returned no subsystem rows."
      />
    );
  }
  return (
    <div className="tp-config-list">
      {rows.map((row) => (
        <div className="tp-config-record" key={`${row.key}-${row.label}`}>
          <div>
            <div className="name">{row.label}</div>
            <div className="meta">{row.detail || row.lastSeen || "No detail"}</div>
          </div>
          <div className={`state ${row.state}`}>{row.state}</div>
        </div>
      ))}
    </div>
  );
};

const EventList = ({ events }: { events: EventRow[] }) => {
  if (events.length === 0) {
    return (
      <Unavailable
        title="No matching events"
        detail="The recent-events endpoint is wired; no matching audit events are currently present."
      />
    );
  }
  return (
    <div className="tp-config-list">
      {events.slice(0, 8).map((event, index) => (
        <div
          className="tp-config-record"
          key={`${event.time}-${event.type}-${index}`}
        >
          <div>
            <div className="name">{event.summary}</div>
            <div className="meta">
              {event.time} · {event.actor}
            </div>
          </div>
          <div className="meta">{event.type}</div>
        </div>
      ))}
    </div>
  );
};

const filterEvents = (events: EventRow[], needles: string[]) =>
  events.filter((event) => {
    const haystack = `${event.type} ${event.summary}`.toLowerCase();
    return needles.some((needle) => haystack.includes(needle));
  });

const money = (value: number | undefined) => {
  if (value === undefined || !Number.isFinite(value)) return "n/a";
  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};
